// ─────────────────────────────────────────────────────────────────────────────
// Artist CNTRL, React FOUNDATION dashboard data layer.
//
// Typed functions that fetch each dashboard section by hitting the SAME live
// endpoints the current desktop dashboard uses (site/js/dashboard-wiring.js).
// Each function cites the legacy source it mirrors. These return the typed
// models in lib/types.ts so CD's redesigned screens can consume them directly,
// the data layer is design-independent.
//
// Endpoint map (all on AUTH_WORKER = https://auth.cntrlglobal.com):
//   • fetchCurrentUser()     GET  /api/me                       (wiring fetchUser, ~159)
//   • fetchConnections()     GET  /api/data/connections         (wiring fetchConnections, ~178)
//   • fetchDashboardStats()  POST /api/proxy/stats              (wiring fetchStats, ~203)
//   • fetchOpportunities()   GET  /api/data/opportunities       (wiring fetchManualOpps, ~899)
//   • fetchUpcomingShows()   GET  /api/data/schedule            (wiring fetchShows, ~2604)
//   • fetchMerchSummary()    POST /api/proxy/shopify-summary
//                            POST /api/proxy/shopify-bestsellers (wiring fetchMerchSpotlight, ~3200)
//                            POST /api/proxy/square-summary      (Square fallback path)
// ─────────────────────────────────────────────────────────────────────────────

import { apiGet, apiPost, apiPut, ApiError, AUTH_WORKER, STATS_WORKER, primeDataBatch } from './api';
import { authHeaders, getCurrentBandId } from './auth';
/* asset-artwork 2026-08-22 — the dashboard card resolves cover art the same
   way the Releases hub does (Asset Library cover first, with a fresh viewer
   token on stream URLs), instead of only the legacy artwork fields. */
import { resolveArtworkUrl, type Release as ReleasesRelease } from './releases';
import { applyViewAs } from './viewAs';
import { visibleShows } from './booking';

// The stats worker's GET /assets resolves the band from the caller's TOKEN, not
// the X-Band-Id header, so a roster viewer must pass `?u=<bandId>` to read the
// VIEWED artist's press kit (legacy assets-wiring.js loadAssets :156-165;
// cntrl-stats-worker.js :2765). Mirror that here for every /assets read.
/* boot-perf 2026-07-18: assetsPath() retired, every dashboard-side photo
   read now goes through the tiny /assets?meta=1 projection (fetchPhotoMeta)
   instead of the full multi-MB blob. */
import type {
  MeResponse,
  User,
  Connections,
  ConnectionBlob,
  DashboardStats,
  DashboardSpotlight,
  StatTrack,
  PlatformStats,
  PhotoPlacements,
  PhotoSurface,
  PhotoSlotKey,
  SpotlightMover,
  Opportunity,
  OpportunitiesResponse,
  Contact,
  ScheduleEvent,
  ScheduleResponse,
  UpcomingShow,
  MerchSummary,
  MerchBestseller,
  ShopifySummaryResponse,
  ShopifyBestsellersResponse,
  SquareSummaryResponse,
  DailyTrendsResponse,
  TrendSnapshot,
  TrendPlatformSnapshot,
  MoverPlatform,
  BiggestMover,
  ReleaseRecord,
  ReleasesResponse,
  ReleaseAnalyticsResponse,
  SongstatsResponse,
  StreamsSpotifyResponse,
  TrackPopularityResponse,
  RecentReleaseDetail,
  PlaylistPlacement,
  ArtistMusicResponse,
  SpotifyStats,
  StandaloneTaskRecord,
  ReleaseChecklistItem,
  TodoTask,
} from './types';

// ── User ─────────────────────────────────────────────────────────────────────

/**
 * GET /api/me → the signed-in user.
 * Mirrors dashboard-wiring.js fetchUser() which reads `data.user` (the worker
 * wraps the user in { success, user }).
 */
// Module cache of the signed-in user. /api/me is constant for the session, so the
// first fetch (Dashboard/TopNav on load) warms this and later screens read it
// synchronously, avoiding a fail-closed flash (e.g. Schedule briefly rendering
// the no-money Calendar before the role resolves on every Booking-tab click).
let _cachedUser: User | undefined;
// getCachedUser / fetchCurrentUser return the user AFTER the jedi view-as
// override (applyViewAs) so every screen's role gates render as the simulated
// seat. The RAW user stays in _cachedUser so the override (which keys off the
// real email) keeps working and toggling back to god mode is lossless.
export function getCachedUser(): User | undefined { return applyViewAs(_cachedUser); }

export async function fetchCurrentUser(): Promise<User> {
  const res = await apiGet<MeResponse>('auth', '/api/me');
  _cachedUser = res.user;
  return applyViewAs(res.user) ?? res.user;
}

/** The real signed-in user, ignoring any jedi view-as override (for the
 *  switcher's own email gate, which must read the true identity). */
export function getRawCachedUser(): User | undefined { return _cachedUser; }

// ── Connections ───────────────────────────────────────────────────────────────

/**
 * GET /api/data/connections → the connector map (token-stripped public fields).
 * Mirrors fetchConnections()'s defensive unwrap: the worker may wrap the blob in
 * up to a few layers of { data } / { value }, and the innermost value may be a
 * JSON string.
 */
export async function fetchConnections(): Promise<Connections> {
  const raw = await apiGet<unknown>('auth', '/api/data/connections');
  let d: unknown = raw;
  for (let i = 0; i < 4; i++) {
    if (d && typeof d === 'object' && !Array.isArray(d)) {
      const obj = d as Record<string, unknown>;
      if (obj.data !== undefined) {
        d = obj.data;
        continue;
      }
      if (obj.value !== undefined) {
        d = obj.value;
        continue;
      }
    }
    break;
  }
  if (typeof d === 'string') {
    try {
      d = JSON.parse(d);
    } catch {
      d = {};
    }
  }
  return d && typeof d === 'object' && !Array.isArray(d) ? (d as Connections) : {};
}

// ── Stats / spotlight ─────────────────────────────────────────────────────────

/** Build the POST body for /api/proxy/stats from the connections map. */
function statsBodyFromConnections(c: Connections): Record<string, string | number> {
  const spotify = c.spotify as ConnectionBlob | undefined;
  const youtube = c.youtube as ConnectionBlob | undefined;
  const tiktok = c.tiktok as ConnectionBlob | undefined;
  const meta = c.meta as ConnectionBlob | undefined;
  return {
    spotify_id: spotify?.id ?? '',
    youtube_id: youtube?.id ?? '',
    tiktok_user: tiktok?.id ?? '',
    meta_page_id: meta?.pageId ?? '',
    meta_ig_id: meta?.igUserId ?? '',
  };
}

/** Sum follower-style counts across every connected platform. */
function sumFollowers(stats: DashboardStats): number {
  let total = 0;
  total += stats.instagram?.followers ?? 0;
  total += stats.facebook?.followers ?? 0;
  total += stats.tiktok?.followers ?? 0;
  total += stats.youtube?.subscribers ?? 0;
  total += stats.threads?.followers ?? 0;
  total += stats.spotify?.followers ?? 0;
  return total;
}

/**
 * Rank the biggest current performer (platform or track) by raw size. The
 * legacy renderSpotlight ranks by 30-day % growth when trend snapshots exist
 * (/api/proxy/stats here doesn't return the trends bucket the legacy code reads
 * from a separate source), so this foundation ranks by raw value, the day-1
 * fallback renderSpotlight itself uses before snapshots accumulate.
 */
function computeTopMover(stats: DashboardStats): SpotlightMover | null {
  const pool: SpotlightMover[] = [];
  const push = (
    kind: SpotlightMover['kind'],
    key: string,
    label: string,
    src: string,
    value: number,
    unit: string,
  ) => {
    if (value > 0) pool.push({ kind, key, label, src, value, unit, delta: null });
  };

  push('platform', 'instagram', 'Instagram', 'Instagram', stats.instagram?.followers ?? 0, 'followers');
  push('platform', 'tiktok', 'TikTok', 'TikTok', stats.tiktok?.followers ?? 0, 'followers');
  push('platform', 'facebook', 'Facebook', 'Facebook', stats.facebook?.followers ?? 0, 'followers');
  push('platform', 'youtube', 'YouTube', 'YouTube', stats.youtube?.subscribers ?? 0, 'subscribers');
  push('platform', 'threads', 'Threads', 'Threads', stats.threads?.followers ?? 0, 'followers');
  push('platform', 'spotify', 'Spotify', 'Spotify', stats.spotify?.followers ?? 0, 'followers');

  const tracks = stats.spotify?.top_tracks ?? [];
  for (const t of tracks.slice(0, 12)) {
    const streams = Number(t.streams ?? t.value ?? 0);
    const key = String(t.id ?? t.track_id ?? t.name ?? '');
    push('track', key, t.name ?? t.title ?? 'Untitled', 'Spotify', streams, 'streams');
  }

  if (!pool.length) return null;
  pool.sort((a, b) => b.value - a.value);
  return pool[0];
}

/**
 * POST /api/proxy/stats → the per-platform stats, plus a derived spotlight
 * summary (total followers, Spotify headline numbers, top mover).
 * Mirrors dashboard-wiring.js fetchStats() (body built from connections) +
 * renderSpotlight()'s ranking.
 *
 * @param connections the result of fetchConnections() (drives the request body).
 * @param opts.bustCache pass true to bypass the worker's stats cache (`nocache:1`).
 */
export async function fetchDashboardStats(
  connections: Connections,
  opts: { bustCache?: boolean } = {},
): Promise<DashboardSpotlight> {
  const body = statsBodyFromConnections(connections);
  if (opts.bustCache) body.nocache = 1;
  const stats = await apiPost<DashboardStats>('auth', '/api/proxy/stats', body);
  // audit-b2 2026-07-05, mirror the legacy daily snapshot writer (see
  // maybeFireDailySnapshot). Fire-and-forget on FRESH network stats only (this
  // apiPost always hits the network, never a cache paint), matching the
  // legacy loadStats() → fireDailySnapshot placement. Never blocks or fails
  // the stats read.
  void maybeFireDailySnapshot(stats).catch(() => {});
  return {
    totalFollowers: sumFollowers(stats),
    spotifyFollowers: stats.spotify?.followers ?? 0,
    monthlyListeners: stats.spotify?.monthly_listeners ?? 0,
    streamsTotal: stats.spotify?.streams_total ?? 0,
    topMover: computeTopMover(stats),
    raw: stats,
  };
}

// audit-b2 2026-07-05, BIGGEST MOVERS never accumulated on React-only bands:
// computeBiggestMovers reads user_data['daily_trends'], whose ONLY writer was
// the LEGACY dashboard's snapshot POST (dashboard-wiring.js fireDailySnapshot
// :2917 → POST /api/proxy/ai-cache-stats; the worker appends today's
// YYYY-MM-DD-keyed snapshot to daily_trends, prunes >30 days, and refreshes
// stats_cache for the roster rail, cntrl-auth-worker.js
// handleProxyAiCacheStats :35926-35997). React never wrote it, so the 30-day
// delta math had nothing to compare against, forever. Mirror the legacy
// writer with the SAME endpoint + body shape, guarded:
//   · once per band per page load (legacy ac._snapshotFiredByBand),
//   · at most once per DAY per band, skip when daily_trends' last entry is
//     already today's UTC key (the worker keys days by toISOString, :35977),
//   · NEVER with all-zero/empty placeholder stats (Bible 06.4, no fabricated
//     data): a fresh/disconnected account writes nothing and the movers grid
//     keeps its honest empty state.
// Band scoping rides authHeaders' X-Band-Id (apiPost) exactly like the legacy
// iframe path; the worker resolves the write target via resolveTargetBandId.
/* leak-sweep 2026-07-17 (Ryan: "everyone has this leaked data from Whitsett.
   I thought we stopped all the leaks"), shared per-ACCOUNT localStorage
   scope. NEVER a bare 'self': that key is shared by every login on a browser
   profile, which is exactly how Whitsett's data bled into Relient K twice
   (band profile in June, Content I'm trying today). Band id when acting in a
   roster band, else the signed-in user's id, else null, callers SKIP
   persistent storage entirely when null. */
export function accountStorageScope(): string | null {
  const band = getCurrentBandId();
  if (band) return band;
  const u = getCachedUser();
  return u && u.id ? 'u_' + String(u.id) : null;
}

const _snapshotFiredByBand = new Set<string>();

async function maybeFireDailySnapshot(stats: DashboardStats): Promise<void> {
  const bandKey = getCurrentBandId() || 'self';
  if (_snapshotFiredByBand.has(bandKey)) return;

  // Same body fireDailySnapshot posts (dashboard-wiring.js :2924-2943).
  const sp = stats.spotify ?? {};
  const tracks: Record<string, number> = {};
  // trending 2026-07-06, track names + artwork ride along so the worker's
  // daily Trending sweep can say '"<Song>" had a major jump in streams'
  // (and thumb the pill) instead of skipping unnamed track ids. The worker
  // sanitizes + caps this map (handleProxyAiCacheStats tracks_meta).
  const trackMeta: Record<string, { name: string; art: string }> = {};
  for (const t of sp.top_tracks ?? []) {
    const id = String(t.id ?? t.track_id ?? t.name ?? '');
    if (id) {
      tracks[id] = Number(t.streams ?? t.value ?? 0);
      trackMeta[id] = {
        name: String(t.name ?? t.title ?? ''),
        art: trackArtwork(t),
      };
    }
  }
  /* stats-provenance 2026-07-18: stamp WHICH spotify artist these numbers
     were computed from, so the worker can refuse a stale client's post
     (the Mae/Maelo re-poison loop). Best-effort, the worker treats a
     missing stamp as legacy and accepts. */
  let snapshotSpotifyId = '';
  try {
    const provConn = await fetchConnections();
    const provSp = (provConn as Record<string, { id?: string } | undefined>).spotify;
    snapshotSpotifyId = provSp && provSp.id ? String(provSp.id) : '';
  } catch { /* stamp optional */ }
  const body = {
    spotify_artist_id: snapshotSpotifyId,
    spotify_followers: Number(sp.followers ?? 0),
    spotify_monthly: Number(sp.monthly_listeners ?? 0),
    spotify_streams_total: Number(sp.streams_total ?? 0),
    ig_followers: Number(stats.instagram?.followers ?? 0),
    fb_followers: Number(stats.facebook?.followers ?? 0),
    tiktok_followers: Number(stats.tiktok?.followers ?? 0),
    youtube_subs: Number(stats.youtube?.subscribers ?? 0),
    threads_followers: Number(stats.threads?.followers ?? 0),
    tracks,
    track_meta: trackMeta, // trending 2026-07-06
  };
  // No fabricated data: at least ONE real (positive) number or the snapshot
  // doesn't get written. Don't burn the per-load guard on an empty read, a
  // later fresh fetch this session may carry real numbers.
  const hasReal =
    body.spotify_followers > 0 || body.spotify_monthly > 0 || body.spotify_streams_total > 0 ||
    body.ig_followers > 0 || body.fb_followers > 0 || body.tiktok_followers > 0 ||
    body.youtube_subs > 0 || body.threads_followers > 0 ||
    Object.keys(tracks).some((k) => tracks[k] > 0);
  if (!hasReal) return;

  _snapshotFiredByBand.add(bandKey);

  // Once per day: if the newest daily_trends entry is already today's key,
  // don't re-write (the worker overwrites same-day keys, but re-POSTing adds
  // nothing and could replace a fresher snapshot with this one).
  // UTC key on purpose, it must match the worker's toISOString day keying.
  //
  // snapshot-upgrade 2026-07-17 (Ryan: RK's Audience stuck on "Connect
  // Spotify + Songstats" all day): a brand-new band's FIRST snapshot of the
  // day often fires from a cached-empty stats read, real ml/followers but
  // ZERO tracks, and then blocks the good post-prime snapshot until
  // tomorrow, leaving Top Songs empty for the band's entire first day.
  // Exception: re-fire when we NOW have tracks and today's entry has none
  // (a strict upgrade, the worker overwrites the same-day key).
  const today = new Date().toISOString().slice(0, 10);
  const trends = await fetchDailyTrends().catch(() => ({}) as Record<string, TrendSnapshot>);
  const dates = Object.keys(trends).sort();
  if (dates.length > 0 && dates[dates.length - 1] >= today) {
    const todayEntry = trends[dates[dates.length - 1]] as { tracks?: Record<string, number> } | undefined;
    const todayTrackCount = todayEntry && todayEntry.tracks ? Object.keys(todayEntry.tracks).length : 0;
    const freshTrackCount = Object.keys(tracks).length;
    if (!(freshTrackCount > 0 && todayTrackCount === 0)) return;
  }

  await apiPost<unknown>('auth', '/api/proxy/ai-cache-stats', body);
}

// ── Spotlight background photo (live press shot) ──────────────────────────────
//
// VERIFICATION (per the strict rules):
//   Legacy data source, assets-wiring.js loadAssets() (:153-168):
//     var assetsUrl = STATS_WORKER + '/assets' + (embedBandId ? '?u='+… : '');
//     return fetch(assetsUrl, { headers: { Authorization: 'Bearer '+token } })
//       .then(r => r.ok ? r.json() : {}).then(data => { if (data && !data.error) assets = data; … });
//     // window.acAssets.getAssets() returns this `assets` blob (assets-wiring.js:1532).
//   Legacy client read, dashboard-wiring.js applySpotlightPhoto() (:2122-2124):
//     var ax = (window.acAssets && window.acAssets.getAssets && window.acAssets.getAssets()) || {};
//     var livePhoto = (ax.photo3 && ax.photo3.data)
//                  || (ax.photo2 && ax.photo2.data)
//                  || (ax.photo1 && ax.photo1.data);
//   Worker return, cntrl-stats-worker.js:2837 (GET /assets, authenticated):
//     return jsonResponse(data || {});
//     // data = ASSETS_KV.get('press-kit:'+primaryUserId, 'json')
//     //      = { photo1:{data:'data:image/jpeg;base64,…', name:'…'}, photo2:{…}, photo3:{…}, … }
// React equivalent: GET /assets (auth worker is the wrong base, this lives on
// STATS_WORKER, exactly like assets-wiring.js), read photo3→photo2→photo1.

/**
 * GET https://stats.cntrlglobal.com/assets → the press-kit blob, returning the
 * live spotlight background as a base64 data URL with the SAME precedence the
 * legacy applySpotlightPhoto() uses (photo3 → photo2 → photo1). Returns '' when
 * no press photo has been uploaded (fresh account) → the Spotlight keeps CD's
 * empty .spotlight__bg gradient. Mirrors assets-wiring.js loadAssets().
 */
/** Photo PRESENCE + resolved band uid, the tiny `/assets?meta=1` payload that
 *  replaces the multi-MB blob download. Works in both roster (?u=<bandId>) and
 *  own-band (token) contexts; the worker echoes the resolved uid so we can build
 *  cacheable /photo URLs either way. */
interface PhotoMeta {
  uid: string | null;
  photo1: boolean; photo2: boolean; photo3: boolean;
  /* boot-perf 2026-07-18: meta now also carries photo4/photo5 presence +
     placements so hero surfaces resolve their slot WITHOUT the full blob. */
  photo4?: boolean; photo5?: boolean;
  logo: boolean;
  placements?: PhotoPlacements;
}
async function fetchPhotoMeta(bandIdOverride?: string): Promise<PhotoMeta | null> {
  const bandId = bandIdOverride || getCurrentBandId();
  const path = '/assets?meta=1' + (bandId ? '&u=' + encodeURIComponent(bandId) : '');
  return apiGet<PhotoMeta>('stats', path).catch(() => null);
}
/** Cacheable raw-image URL for one press photo (mirrors the avatar pattern used
 *  in EditShowModal/Releases/contacts: STATS_WORKER/photo?u=<uid>&which=<slot>). */
function photoSrc(uid: string, which: PhotoSlotKey): string {
  /* size=sm, heroes render as dimmed backgrounds; the ~720px variant is
     visually identical there and ~10-20x smaller than the press original. */
  return STATS_WORKER + '/photo?u=' + encodeURIComponent(uid) + '&which=' + which + '&size=sm';
}

// ── Photo placements (Assets "Used for" pill) ─────────────────────────────────
// Per-surface fallback order when the user hasn't explicitly placed a photo. The
// user's chosen slot (blob.placements[surface]) is tried FIRST, then these.
const PLACEMENT_DEFAULTS: Record<PhotoSurface, PhotoSlotKey[]> = {
  dashboard: ['photo3', 'photo2', 'photo1'], // legacy applySpotlightPhoto order
  content: ['photo4'],
  music: ['photo5'],
};

/** Resolve a spotlight surface to a photo data URL: the user's placement choice
 *  first (blob.placements[surface]), then the surface's default slot order; '' if
 *  none uploaded (→ honest gradient fallback, Bible 06.4). Reads the full /assets
 *  blob because placements live there. Band-scoped via assetsPath() (?u= under
 *  impersonation) → an artist switch reads the new artist's photos. */
export async function fetchPlacedPhoto(surface: PhotoSurface, bandId?: string): Promise<string> {
  /* boot-perf 2026-07-18 (Dev prompt phase 2, consolidation): this used to
     pull the FULL multi-MB /assets blob, and the dashboard, content, and
     music heroes each pulled it independently at boot (the duplicate 400-500ms
     stats.cntrlglobal.com/assets rows in the perf log), just to read the
     tiny `placements` map. The meta projection (~200 bytes, edge-cached 1h)
     now carries placements + per-slot presence; resolve the slot here and
     return ONE cacheable /photo URL. Band isolation preserved: an explicit
     bandId rides ?u= exactly like the old blob read. */
  const meta = await fetchPhotoMeta(bandId);
  if (!meta || !meta.uid) return '';
  const chosen = meta.placements ? meta.placements[surface] : undefined;
  const order: PhotoSlotKey[] = chosen
    ? [chosen, ...PLACEMENT_DEFAULTS[surface]]
    : PLACEMENT_DEFAULTS[surface];
  const present: Record<string, boolean | undefined> = {
    photo1: meta.photo1, photo2: meta.photo2, photo3: meta.photo3,
    photo4: meta.photo4, photo5: meta.photo5,
  };
  for (const key of order) {
    if (present[key]) return photoSrc(meta.uid, key);
  }
  return '';
}

/** Dashboard headline spotlight background. Honors blob.placements.dashboard,
 *  falling back to the legacy photo3 → photo2 → photo1 order. */
export async function fetchSpotlightPhoto(): Promise<string> {
  return fetchPlacedPhoto('dashboard');
}

/**
 * GET https://stats.cntrlglobal.com/assets → the press-kit blob, returning ONLY
 * "Live photo 1" (the band's primary press photo) as a base64 data URL. Unlike
 * fetchSpotlightPhoto (which prefers photo3→photo2→photo1), this is photo1
 * SPECIFICALLY, the canonical band shot used as a card background. Returns ''
 * when no photo1 has been uploaded (fresh account) → callers keep their
 * gradient-only look (honest fallback, Bible 06.4). Band-scoped automatically:
 * apiGet → authHeaders → X-Band-Id from the active band, so a roster artist
 * switch reads the new artist's photo, never the previous one.
 */
export async function fetchLivePhoto1(): Promise<string> {
  const meta = await fetchPhotoMeta();
  if (!meta || !meta.uid || !meta.photo1) return '';
  return photoSrc(meta.uid, 'photo1');
}

/** Music Streaming hero background. Honors blob.placements.music, falling back to
 *  "Alt Photo 2" (photo5). */
export async function fetchLivePhoto5(bandId?: string): Promise<string> {
  return fetchPlacedPhoto('music', bandId);
}

// ── Opportunities ─────────────────────────────────────────────────────────────

/** Unwrap a user_data blob response → its array value (mirrors fetchManualOpps). */
function unwrapBlobArray<T>(
  json: { success?: boolean; data?: { value?: unknown } } | null,
): T[] {
  if (!json || !json.success || !json.data) return [];
  let raw: unknown = json.data.value;
  if (typeof raw === 'string') {
    try {
      raw = JSON.parse(raw);
    } catch {
      raw = [];
    }
  }
  return Array.isArray(raw) ? (raw as T[]) : [];
}

/**
 * GET /api/data/opportunities → the band-shared opportunities list.
 * Mirrors the band-opps half of fetchManualOpps() (the personal
 * /api/me/data/invites_received bucket is a separate concern left for the
 * inbox/opps screen to merge later, this returns the band-shared source).
 */
export async function fetchOpportunities(): Promise<Opportunity[]> {
  const json = await apiGet<OpportunitiesResponse>('auth', '/api/data/opportunities');
  return unwrapBlobArray<Opportunity>(json);
}

// ── Opportunity writes (Add / Edit / Delete) ──────────────────────────────────
// Mirrors the legacy/mobile flow: a manual opp lives in /api/data/opportunities
// and (when it has a date) is mirrored into /api/data/schedule as its type so it
// surfaces on the calendar (dashboard.html ~11802 appendOpportunityToSchedule,
// ~11927 saveManualOpps). Delete must scrub BOTH blobs or the row ghosts (the
// exact bug a deleted rehearsal hit).

type SchedRow = Record<string, unknown>;

/** Read the raw schedule array (handles the {value:[]} blob envelope). */
async function readScheduleArray(): Promise<SchedRow[]> {
  const sched = await apiGet<{ data?: { value?: unknown[] } | unknown[] }>('auth', '/api/data/schedule');
  const raw = sched && sched.data;
  const arr = Array.isArray(raw) ? raw : (raw as { value?: unknown[] } | undefined)?.value;
  /* hide-for-me 2026-07-17: skip rows the current user soft-deleted (the
     schedule gate stamps responses[uid]='pass', see lib/booking.ts). */
  const uid = String(getCachedUser()?.id ?? '');
  const rows = Array.isArray(arr) ? (arr as SchedRow[]) : [];
  return uid
    ? rows.filter((r) => !((r as { responses?: Record<string, string> }).responses?.[uid] === 'pass'))
    : rows;
}

/** Persist the full band-shared opportunities list. */
export async function saveOpportunities(list: ReadonlyArray<Opportunity>): Promise<void> {
  await apiPut('auth', '/api/data/opportunities', { value: list });
}

/** Mirror one opportunity into the schedule blob (idempotent upsert by id). */
async function mirrorOpportunityToSchedule(opp: Opportunity): Promise<void> {
  if (!opp.date) return;
  const arr = await readScheduleArray();
  const ev: SchedRow = {
    id: opp.id,
    type: opp.type,
    name: opp.title,
    title: opp.title,
    date: opp.date,
    time: opp.time ?? null,
    endTime: opp.endTime ?? null,
    address: opp.address ?? null,
    venue: opp.address ?? null,
    venueAddress: opp.address ?? null,
    money: opp.money ?? null,
    prType: opp.prType ?? null,
    notes: opp.notes ?? '',
    confirmed: true,
    fromOpportunity: true,
    opportunityId: opp.id,
  };
  const idx = arr.findIndex((e) => String(e?.id) === String(opp.id));
  if (idx >= 0) arr[idx] = { ...arr[idx], ...ev };
  else arr.push(ev);
  await apiPut('auth', '/api/data/schedule', { value: arr });
}

/** Create or update one opportunity (by id) + mirror it to the schedule. */
export async function upsertOpportunity(opp: Opportunity): Promise<void> {
  const list = await fetchOpportunities().catch(() => [] as Opportunity[]);
  const idx = list.findIndex((o) => String(o.id) === String(opp.id));
  const next = list.slice();
  if (idx >= 0) next[idx] = { ...list[idx], ...opp };
  else next.push(opp);
  await saveOpportunities(next);
  await mirrorOpportunityToSchedule(opp).catch(() => { /* mirror is best-effort */ });
}

/** Delete one opportunity everywhere, the opportunities blob AND its schedule
 *  mirror (matched by id or opportunityId). Prevents the ghost-row bug. */
export async function deleteOpportunityEverywhere(id: string): Promise<void> {
  const list = await fetchOpportunities().catch(() => [] as Opportunity[]);
  const nextOpps = list.filter((o) => String(o.id) !== String(id));
  if (nextOpps.length !== list.length) await saveOpportunities(nextOpps);
  try {
    const arr = await readScheduleArray();
    const next = arr.filter(
      (e) => String(e?.id) !== String(id) && String(e?.opportunityId ?? '') !== String(id),
    );
    if (next.length !== arr.length) await apiPut('auth', '/api/data/schedule', { value: next });
  } catch { /* schedule scrub is best-effort */ }
}

// ── Contacts (avatar source for opp invitees) ─────────────────────────────────
//
// VERIFICATION (per the strict rules):
//   Legacy caller, dashboard-wiring.js:1592:
//     fetch(AUTH_WORKER + '/api/data/contacts', { headers: authHeaders(false) })
//   Legacy client read, dashboard-wiring.js:1742-1744 (per contact `c`):
//     var avatarUrl = c.avatarUrl || c.avatar_url || c.photoUrl || c.photo_url || '';
//     // matched to an invitee by id/userId/teamUserId/email (the same keys the
//     // picker stamps onto the invitee at :1762-1772).
//   Worker return, cntrl-auth-worker.js (GET /api/data/*): jsonResponse({ success, key, data }).
//     data.value = the contacts array (same blob envelope as opportunities).

/** GET /api/data/contacts → the band contacts blob. */
export async function fetchContacts(): Promise<Contact[]> {
  const json = await apiGet<{ success?: boolean; data?: { value?: unknown } }>(
    'auth',
    '/api/data/contacts',
  ).catch(() => null);
  return unwrapBlobArray<Contact>(json);
}

/**
 * Build a lookup of every contact identifier → their avatar url, so an opp
 * invitee stored without a baked-in avatar still resolves a photo at render.
 * Avatar precedence mirrors the legacy picker (dashboard-wiring.js:1744):
 * avatarUrl || avatar_url || photoUrl || photo_url.
 */
export function buildInviteeAvatarMap(contacts: ReadonlyArray<Contact>): Record<string, string> {
  const map: Record<string, string> = {};
  for (const c of contacts) {
    const url = c.avatarUrl || c.avatar_url || c.photoUrl || c.photo_url;
    if (!url) continue;
    for (const id of [c.id, c.userId, c.teamUserId, c.email]) {
      if (id != null && id !== '') map[String(id)] = url;
    }
  }
  return map;
}

// ── Upcoming shows ────────────────────────────────────────────────────────────

/** Resolve a flyer image URL with the same precedence as flyerSrc() (~2473). */
function flyerUrl(ev: ScheduleEvent): string {
  if (ev.flyerKey) {
    return 'https://stats.cntrlglobal.com/serve-file?key=' + encodeURIComponent(ev.flyerKey);
  }
  if (ev.flyerUrl) return ev.flyerUrl;
  if (ev.flyer) return ev.flyer;
  return '';
}

/** Human kind label for a schedule event (mirrors eventKindLabel(), ~2504). */
function eventKindLabel(ev: ScheduleEvent): string {
  if (ev.kind) return String(ev.kind);
  if (ev.isOffDay || ev.type === 'off') return 'Off day';
  if (ev.is_rehearsal || ev.type === 'rehearsal') return 'Rehearsal';
  if (ev.type === 'content') return 'Content';
  if (ev.type === 'write') return 'Write';
  if (ev.type === 'pr') return 'PR';
  if (ev.type === 'placement') return 'Placement';
  if (ev.type === 'tour') return 'Tour';
  if (ev.type === 'sync') return 'Sync';
  if (ev.type === 'press') return 'Press';
  if (ev.type === 'other') return 'Other';
  if (ev.confirmed === false) return 'Hold';
  return 'Show';
}

/** Normalize a raw schedule event into a display-ready UpcomingShow row. */
function toUpcomingShow(ev: ScheduleEvent): UpcomingShow {
  const venue = ev.venue || ev.name || '';
  const city = ev.city || '';
  const kind = eventKindLabel(ev);
  const title = city || venue || ev.name || kind;
  const subtitle = city && venue ? venue : kind;
  return {
    id: ev.id,
    date: ev.date || '',
    title,
    subtitle,
    kind,
    flyerUrl: flyerUrl(ev),
    raw: ev,
  };
}

/**
 * GET /api/data/schedule → the next N future events (Upcoming widget). Shows
 * EVERY upcoming event type, shows, holds, rehearsals, and other opp-spawned
 * events, sorted ascending by date (the kind pill distinguishes them). Reads the
 * full schedule blob, drops only past-dated entries, sorts, slices.
 *
 * @param limit how many upcoming rows to return.
 */
export async function fetchUpcomingShows(limit: number = 5): Promise<UpcomingShow[]> {
  const res = await apiGet<ScheduleResponse | ReadonlyArray<ScheduleEvent>>('auth', '/api/data/schedule');

  // Defensive unwrap, bare array, { data: [...] }, { value: [...] },
  // or { data: { value: [...] } } (mirrors fetchShows' unwrap).
  let arr: ReadonlyArray<ScheduleEvent> = [];
  if (Array.isArray(res)) {
    arr = res;
  } else if (res && typeof res === 'object') {
    const obj = res as ScheduleResponse;
    const data: unknown = obj.data;
    if (Array.isArray(data)) {
      arr = data as ReadonlyArray<ScheduleEvent>;
    } else if (data && typeof data === 'object') {
      const nested = (data as { value?: ReadonlyArray<ScheduleEvent> }).value;
      if (Array.isArray(nested)) arr = nested;
    } else if (Array.isArray(obj.value)) {
      arr = obj.value;
    }
  }

  /* show-scope 2026-07-29 — narrow to this seat's dates before any other
     filtering. DISPLAY ONLY; fetchUpcomingShows never writes. Viewer resolved
     explicitly: getCachedUser is empty on a cold load until fetchCurrentUser
     lands, and a null default would blank Upcoming for the artist too. */
  const _viewer = getCachedUser() || (await fetchCurrentUser().catch(() => undefined));
  arr = visibleShows(arr, _viewer);

  const todayIso = new Date().toISOString().slice(0, 10);
  /* hide-for-me 2026-07-18 (Ryan: "we deleted this show, why is it still
     showing", the dashboard Upcoming card): a non-full-access member's delete
     is stored as responses[uid]='pass' by the schedule gate. Every OTHER
     schedule surface (readScheduleArray above, calendar.ts, spotlights.ts,
     booking.ts isHiddenForMe) skips those rows, but THIS fetch never did, so a
     soft-deleted show kept rendering on the dashboard for the very person who
     deleted it. Apply the same central predicate. */
  const _uid = String(getCachedUser()?.id ?? '');
  const upcoming = arr
    .filter((ev) => {
      if (!ev || !ev.date) return false;
      if (ev.date < todayIso) return false;
      if (_uid && (ev as { responses?: Record<string, string> }).responses?.[_uid] === 'pass') return false;
      return true; // all types: shows, holds, rehearsals/opps
    })
    .slice() // copy before sort (input is readonly)
    .sort((a, b) => (a.date || '').localeCompare(b.date || ''));

  return upcoming.slice(0, limit).map(toUpcomingShow);
}

/** Opp types that are calendar events (belong in Upcoming), not booking-funnel. */
const UPCOMING_OPP_TYPES = new Set(['rehearsal', 'content', 'placement', 'pr', 'write']);

/**
 * Convert a calendar-type opportunity (rehearsal/content/placement/pr/write)
 * into an Upcoming row, rehearsals etc. live in /api/data/opportunities, not the
 * schedule blob, so Upcoming has to pull them in to show "all types". Strips the
 * redundant "<Type> · " title prefix like the Opportunities card does.
 */
function oppToUpcoming(opp: Opportunity): UpcomingShow | null {
  const date = opp.date || '';
  if (!date) return null;
  const t = String(opp.type || '').toLowerCase();
  if (!UPCOMING_OPP_TYPES.has(t)) return null;
  const kind = t.charAt(0).toUpperCase() + t.slice(1);
  let title = String(opp.title || '').trim();
  const pfx = kind + ' · ';
  if (title.toLowerCase().startsWith(pfx.toLowerCase())) title = title.slice(pfx.length).trim();
  const addr = String(opp.address || '').trim();
  if (!title) title = addr || kind;
  const subtitle = addr && !title.includes(addr) && !addr.includes(title) ? addr : '';
  return { id: 'opp-' + String(opp.id), date, title, subtitle, kind, flyerUrl: '', raw: opp };
}

/**
 * Merge schedule-based upcoming rows with calendar-type opportunities, dedupe by
 * date+title, sort ascending, slice. So Upcoming shows shows, holds AND
 * rehearsals/opps together.
 */
export function mergeUpcoming(
  shows: ReadonlyArray<UpcomingShow>,
  opps: ReadonlyArray<Opportunity>,
  limit: number = 5,
): UpcomingShow[] {
  const todayIso = new Date().toISOString().slice(0, 10);
  /* orphan-mirror guard 2026-07-06 (found live): a deleted opportunity's
     schedule MIRROR row can survive a partial delete (e.g. the opps-blob
     write landed but the schedule scrub failed mid-outage). Those orphans
     have NO edit/delete surface anywhere, the opp modal died with the opp,
     so they'd sit in Upcoming forever. Never render a mirror row whose
     parent opportunity no longer exists. Display-side only; the schedule
     blob is untouched (no write-on-read). */
  const oppIds = new Set(opps.map((o) => String(o.id)));
  const liveShows = shows.filter((s) => {
    const raw = s.raw as { fromOpportunity?: boolean; opportunityId?: string | number } | undefined;
    const mirrorOf = raw && (raw.opportunityId ?? (raw.fromOpportunity ? s.id : undefined));
    return mirrorOf == null || oppIds.has(String(mirrorOf));
  });
  const seen = new Set(liveShows.map((s) => s.date + '|' + s.title.toLowerCase()));
  const merged: UpcomingShow[] = [...liveShows];
  for (const opp of opps) {
    const row = oppToUpcoming(opp);
    if (!row || row.date < todayIso) continue;
    const key = row.date + '|' + row.title.toLowerCase();
    if (seen.has(key)) continue;
    seen.add(key);
    merged.push(row);
  }
  return merged.sort((a, b) => a.date.localeCompare(b.date)).slice(0, limit);
}

// ── Merch summary ─────────────────────────────────────────────────────────────

/**
 * Merch summary via the Shopify (preferred) or Square proxy.
 * Mirrors fetchMerchSpotlight() (~3200): if a Shopify store is connected, POST
 * /api/proxy/shopify-summary + /api/proxy/shopify-bestsellers in parallel with
 * { tz, pos_only:false }; else if Square is connected, POST
 * /api/proxy/square-summary with { tz }; else return an empty summary.
 *
 * @param connections result of fetchConnections() (decides which proxy to hit).
 */
/** merch-both 2026-07-18: a provider's assigned sales channel. Absent = 'both';
    'none' = connected but not counted (the other provider owns everything). */
type MerchChannel = 'both' | 'online' | 'pos' | 'none';
function merchChannelOf(c: ConnectionBlob | undefined): MerchChannel {
  const v = String((c?.channels as string) || 'both').toLowerCase();
  return v === 'online' || v === 'pos' || v === 'none' ? v : 'both';
}

interface MerchPart {
  channel: MerchChannel;
  today: number;
  sales30d: number;
  lifetime: number | null;
  pending: number;
  bestsellers: MerchBestseller[];
  currency: string;
}

/**
 * Merch summary across BOTH providers (merch-both 2026-07-18, Ryan). Each
 * connected provider is fetched scoped to the sales channel the band assigned
 * it (Shopify.channels / Square.channels), then summed so online + POS = the
 * band's true merch total with no double-count. When a true split exists (one
 * provider owns online, the other POS) the per-channel 30-day figures are set
 * so the card can show "Online $X · POS $Y".
 */
export async function fetchMerchSummary(connections: Connections): Promise<MerchSummary> {
  const shopify = connections.shopify as ConnectionBlob | undefined;
  const square = connections.square as ConnectionBlob | undefined;
  /* fourthwall 2026-07-18, provider #3. Credential connect; the public blob
     keeps apiUser + connected (password stripped server-side). */
  const fourthwall = connections.fourthwall as ConnectionBlob | undefined;
  const hasShopify = !!(shopify && shopify.store);
  const hasSquare = !!(square && square.merchantId);
  const hasFourthwall = !!(fourthwall && (fourthwall.connected || fourthwall.apiUser));

  const tz =
    (typeof Intl !== 'undefined' && Intl.DateTimeFormat().resolvedOptions().timeZone) ||
    'America/Chicago';

  const parts: MerchPart[] = [];
  const jobs: Array<Promise<void>> = [];

  if (hasShopify && merchChannelOf(shopify) !== 'none') {
    const ch = merchChannelOf(shopify);
    jobs.push(
      (async () => {
        /* consolidation 2026-07-14: /api/proxy/shopify-merch composes summary +
           bestsellers server-side into one round trip. Scoped to `ch`. */
        const merged = await apiPost<{ success?: boolean; summary?: ShopifySummaryResponse | null; bestsellers?: ShopifyBestsellersResponse | null }>(
          'auth', '/api/proxy/shopify-merch', { tz, channel: ch, limit: 4 },
        );
        const sum = merged.summary ?? ({} as ShopifySummaryResponse);
        const bs = merged.bestsellers ?? ({} as ShopifyBestsellersResponse);
        const lifeRaw = sum.salesLifetime;
        const life = lifeRaw == null ? null : parseFloat(String(lifeRaw));
        parts.push({
          channel: ch,
          today: parseFloat(String(sum.salesToday ?? 0)) || 0,
          sales30d: parseFloat(String(sum.sales30d ?? 0)) || 0,
          lifetime: life != null && !isNaN(life) ? life : null,
          pending: parseInt(String(sum.openCount ?? 0), 10) || 0,
          bestsellers: (bs.bestsellers ?? []) as MerchBestseller[],
          currency: sum.currency || bs.currency || 'USD',
        });
      })().catch(() => { /* store hiccup, degrade to no data for this provider */ }),
    );
  }

  if (hasSquare && merchChannelOf(square) !== 'none') {
    const ch = merchChannelOf(square);
    jobs.push(
      (async () => {
        const sum = await apiPost<SquareSummaryResponse & { bestsellers?: ReadonlyArray<MerchBestseller> }>(
          'auth', '/api/proxy/square-summary', { tz, channel: ch },
        );
        parts.push({
          channel: ch,
          today: parseFloat(String(sum.salesToday ?? 0)) || 0,
          sales30d: parseFloat(String(sum.sales30d ?? 0)) || 0,
          lifetime: null,
          pending: parseInt(String(sum.pendingCount ?? 0), 10) || 0,
          bestsellers: (sum.bestsellers ?? []) as MerchBestseller[],
          currency: 'USD',
        });
      })().catch(() => { /* degrade */ }),
    );
  }

  /* fourthwall 2026-07-18, online-only (the worker's connect interlock
     guarantees channels ∈ {online, none}); the proxy answers in the
     square-summary shape so this mirrors the Square part exactly. */
  if (hasFourthwall && merchChannelOf(fourthwall) !== 'none') {
    const ch = merchChannelOf(fourthwall);
    jobs.push(
      (async () => {
        const sum = await apiPost<SquareSummaryResponse & { bestsellers?: ReadonlyArray<MerchBestseller>; currency?: string; salesLifetime?: string | number | null }>(
          'auth', '/api/proxy/fourthwall-summary', { tz, channel: ch },
        );
        /* fourthwall-lifetime 2026-07-21: the worker now sums all-time orders
           (capped + cached) into salesLifetime; thread it through instead of null. */
        const fwLifeRaw = sum.salesLifetime;
        const fwLife = fwLifeRaw == null ? null : parseFloat(String(fwLifeRaw));
        parts.push({
          channel: ch,
          today: parseFloat(String(sum.salesToday ?? 0)) || 0,
          sales30d: parseFloat(String(sum.sales30d ?? 0)) || 0,
          lifetime: fwLife != null && !isNaN(fwLife) ? fwLife : null,
          pending: parseInt(String(sum.pendingCount ?? 0), 10) || 0,
          bestsellers: (sum.bestsellers ?? []) as MerchBestseller[],
          currency: sum.currency || 'USD',
        });
      })().catch(() => { /* degrade */ }),
    );
  }

  await Promise.all(jobs);

  if (!parts.length) {
    return { currency: 'USD', salesToday: 0, sales30d: 0, pendingOrders: 0, bestsellers: [], posOnly: false, source: 'none' };
  }

  const salesToday = parts.reduce((s, p) => s + p.today, 0);
  const sales30d = parts.reduce((s, p) => s + p.sales30d, 0);
  const pendingOrders = parts.reduce((s, p) => s + p.pending, 0);
  const salesLifetime = parts.reduce<number | null>((acc, p) => (p.lifetime != null ? (acc ?? 0) + p.lifetime : acc), null);

  // Merge best-sellers across providers: dedupe by product id / title, sum
  // units + revenue, keep the top 4 by units.
  const bag: Record<string, MerchBestseller & { units: number; revenue?: number }> = {};
  for (const p of parts) {
    for (const b of p.bestsellers) {
      const rec = b as MerchBestseller & { product_id?: string | number | null; units?: number; revenue?: number };
      const key = String(rec.product_id ?? rec.title ?? 'item');
      if (!bag[key]) {
        bag[key] = { ...(rec as MerchBestseller), units: rec.units ?? 0, revenue: rec.revenue ?? 0 };
      } else {
        bag[key] = { ...bag[key], units: (bag[key].units ?? 0) + (rec.units ?? 0), revenue: (bag[key].revenue ?? 0) + (rec.revenue ?? 0) };
      }
    }
  }
  const bestsellers = Object.values(bag).sort((a, b) => (b.units ?? 0) - (a.units ?? 0)).slice(0, 4) as ReadonlyArray<MerchBestseller>;

  // Per-channel breakdown, each channel is owned by ≤1 provider (the settings
  // interlock guarantees it). Only a TRUE split (one online-owner + one
  // pos-owner) sets both, so the card shows "Online $X · POS $Y".
  const onlinePart = parts.find((p) => p.channel === 'online');
  const posPart = parts.find((p) => p.channel === 'pos');
  const hasSplit = !!onlinePart && !!posPart;
  const onlineSales30d = hasSplit ? (onlinePart as MerchPart).sales30d : null;
  const posSales30d = hasSplit ? (posPart as MerchPart).sales30d : null;

  const source: MerchSummary['source'] =
    parts.length > 1 ? 'both' : hasShopify ? 'shopify' : hasSquare ? 'square' : 'fourthwall';

  return {
    currency: parts.find((p) => p.currency)?.currency || 'USD',
    salesToday,
    sales30d,
    salesLifetime,
    pendingOrders,
    bestsellers,
    posOnly: parts.length === 1 && parts[0].channel === 'pos',
    source,
    onlineSales30d,
    posSales30d,
  };
}

/** Shopify App Pricing status (shopify-billing 2026-07-08). */
export interface ShopifyBillingStatus {
  readonly ok: boolean;
  readonly billing_source: 'stripe' | 'shopify' | string;
  readonly subscription_active?: boolean;
  /** True only for a Shopify-App-Store-sourced account with no active sub.
   *  Stripe/organic accounts are ALWAYS false (never pushed to Shopify Billing). */
  readonly subscription_required: boolean;
  /** Where to send a Shopify-sourced merchant to subscribe (Shopify's hosted
   *  plan page), or null when not required / not resolvable. */
  readonly pricing_url?: string | null;
}

/**
 * GET /api/shopify/billing-status, whether this account needs a Shopify App
 * Pricing subscription (and where to subscribe). Existing/organic Stripe
 * customers always come back subscription_required:false, the invariant.
 * Fails OPEN (no gate) so a billing hiccup never locks a legitimate user out.
 */
export async function fetchShopifyBillingStatus(): Promise<ShopifyBillingStatus> {
  try {
    return await apiGet<ShopifyBillingStatus>('auth', '/api/shopify/billing-status');
  } catch {
    return { ok: false, billing_source: 'stripe', subscription_required: false };
  }
}

// ── Biggest movers (daily_trends → 30-day % growth ranking) ───────────────────
//
// VERIFICATION (per the strict rules):
//   Legacy caller, dashboard-wiring.js:2870:
//     fetch(AUTH_WORKER + '/api/data/daily_trends', { headers: authHeaders(false) })
//   Legacy client read, dashboard-wiring.js:2194-2211, 2238-2249:
//     var dates = trends ? Object.keys(trends).sort() : [];
//     today = trends[dates[dates.length - 1]] || null;
//     function pctDelta(now, then){ var n=Number(now||0),o=Number(then||0);
//                                   if(o<=0) return null; return ((n-o)/o)*100; }
//     var nowVal = (today && today[p.key]) ?
//       Number(today[p.key].followers || today[p.key].subscribers || 0) : p.value;
//     var oldVal = (monthAgo && monthAgo[p.key]) ?
//       Number(monthAgo[p.key].followers || monthAgo[p.key].subscribers || 0) : 0;
//     pool.push({ ... delta: pctDelta(nowVal, oldVal) });
//   Worker write (snapshot), cntrl-auth-worker.js:34577-34585:
//     var snapshot = { spotify:{followers,monthly_listeners,streams_total},
//       instagram:{followers}, facebook:{followers}, tiktok:{followers},
//       youtube:{subscribers}, threads:{followers}, tracks: body.tracks||{} };
//   Worker return (GET), cntrl-auth-worker.js:18553-18557:
//     return jsonResponse({ success: true, key: dataKey, data: data || {} });
// No `delta` field exists in the response, it is computed here exactly like
// pctDelta, ranked desc by delta, falling back to raw value when no snapshots.

/** Brand colors painted behind a mover when it has no thumbnail (:2180-2187). */
const MOVER_BRAND_COLOR: Record<string, string> = {
  spotify: '#1DB954',
  instagram: '#E1306C',
  tiktok: '#000000',
  youtube: '#FF0000',
  facebook: '#1877F2',
  threads: '#000000',
};

/** Spotify track artwork precedence (dashboard-wiring.js:2265). */
function trackArtwork(t: StatTrack): string {
  return t.image || t.artwork || t.image_url || t.album_image || t.cover_art || '';
}

/**
 * GET /api/data/daily_trends → the raw daily snapshot bucket. Mirrors the
 * dashboard-wiring.js:2870 fetch. Returns {} when absent (fresh account).
 */
export async function fetchDailyTrends(): Promise<Record<string, TrendSnapshot>> {
  const res = await apiGet<DailyTrendsResponse>('auth', '/api/data/daily_trends');
  if (res && res.success && res.data && typeof res.data === 'object') {
    return res.data;
  }
  return {};
}

/**
 * Build the "Biggest movers" grid, the exact legacy widget
 * (dashboard-wiring.js:2213-2461): a single pool of social platforms AND
 * Spotify tracks, each carrying a real thumbnail (post image / track artwork),
 * its source platform, and a 30-day growth %. Platforms compare current
 * followers/subscribers vs the ~30-day-old daily_trends snapshot; tracks compare
 * current streams vs the snapshot's per-track stream count. The grid is then
 * picked for cross-platform diversity (1 per source first, Spotify tracks capped
 * at 2) instead of raw rank, so it never shows five Spotify rows. Returns [] when
 * nothing qualifies (empty state).
 *
 * @param trends result of fetchDailyTrends().
 * @param stats  the live DashboardStats (current counts + top_tracks/top_videos).
 * @param limit  how many movers to return (CD's grid shows 5).
 */
/** The platform label + source string for a video mover. */
function videoSrc(platform: string): { platform: MoverPlatform; label: string } {
  if (platform === 'tiktok') return { platform: 'tiktok', label: 'TikTok' };
  if (platform === 'youtube') return { platform: 'youtube', label: 'YouTube' };
  if (platform === 'instagram') return { platform: 'instagram', label: 'Instagram' };
  return { platform: 'youtube', label: platform || 'Video' };
}

/** The Monday (YYYY-MM-DD) of the week containing `d`. Week starts Monday. */
function mondayOf(d: Date): string {
  const x = new Date(d.getFullYear(), d.getMonth(), d.getDate());
  const dow = x.getDay(); // 0 Sun … 6 Sat
  x.setDate(x.getDate() - (dow === 0 ? 6 : dow - 1));
  return isoOf(x);
}
function isoOf(d: Date): string {
  return (
    d.getFullYear() +
    '-' +
    String(d.getMonth() + 1).padStart(2, '0') +
    '-' +
    String(d.getDate()).padStart(2, '0')
  );
}

/**
 * Cold-start fallback: no usable week-over-week baseline yet (fresh tracking, or
 * the per-video capture hasn't accumulated two Mondays). Show the current top
 * content items ranked by raw count so the grid is never empty, no `gain`/delta
 * until real history exists.
 */
// One card per platform: from a pool ALREADY sorted best-first, keep only the
// leading (biggest) mover for each platform, then cap at `limit`. Guarantees the
// grid never shows two Spotify tracks, each platform gets its single top mover.
function onePerPlatform(sorted: BiggestMover[], limit: number): BiggestMover[] {
  const seen = new Set<string>();
  const out: BiggestMover[] = [];
  for (const m of sorted) {
    if (seen.has(m.platform)) continue;
    seen.add(m.platform);
    out.push(m);
    if (out.length >= limit) break;
  }
  return out;
}

function liveMoverFallback(stats: DashboardStats, limit: number): BiggestMover[] {
  const pool: BiggestMover[] = [];
  for (const t of stats.spotify?.top_tracks ?? []) {
    const streams = Number(t.streams ?? t.value ?? t.streamCount ?? 0);
    if (streams <= 0) continue;
    pool.push({
      kind: 'track', platform: 'spotify', label: t.name || t.title || 'Untitled',
      src: 'Spotify', value: streams, unit: 'streams', thumb: trackArtwork(t),
      thumbColor: MOVER_BRAND_COLOR.spotify, delta: null,
    });
  }
  for (const key of ['tiktok', 'youtube'] as const) {
    const vids = (stats[key] as PlatformStats | undefined)?.top_videos ?? [];
    for (const v of vids) {
      const views = Number(v.plays ?? v.views ?? 0);
      if (views <= 0) continue;
      const s = videoSrc(key);
      pool.push({
        kind: 'video', platform: s.platform, label: v.title || v.caption || v.name || 'Video',
        src: s.label, value: views, unit: 'views',
        thumb: v.thumbnail_url || v.thumbnail || v.image_url || v.image || '',
        thumbColor: MOVER_BRAND_COLOR[key] || '#444', delta: null,
      });
    }
  }
  pool.sort((a, b) => b.value - a.value);
  return onePerPlatform(pool, limit);
}

/**
 * Build the "Biggest movers" grid, the single biggest CONTENT movers of the week
 * (2026-07-07 rewrite). Ranks individual items, a video by its weekly VIEW gain,
 * a song by its weekly STREAM gain, using Monday-anchored week-over-week deltas:
 * the snapshot on/just-before THIS Monday vs the one on/just-before LAST Monday.
 * The numbers hold steady through the week and refresh every Monday, so teams
 * start the week with fresh data. New content (no prior-Monday baseline) counts
 * its full view/stream count as this-week gain. Falls back to the current top
 * items (no delta) until two Mondays of history exist.
 *
 * @param trends result of fetchDailyTrends().
 * @param stats  the live DashboardStats (used for the cold-start fallback).
 * @param limit  how many movers to return (CD's grid shows 5).
 */
export function computeBiggestMovers(
  trends: Record<string, TrendSnapshot>,
  stats: DashboardStats,
  limit: number = 5,
): BiggestMover[] {
  const dates = Object.keys(trends).sort();
  if (!dates.length) return liveMoverFallback(stats, limit);

  const now = new Date();
  const thisMon = mondayOf(now);
  const lastMonDate = new Date(now);
  lastMonDate.setDate(lastMonDate.getDate() - 7);
  const lastMon = mondayOf(lastMonDate);

  const onOrBefore = (target: string): string | undefined => {
    let best: string | undefined;
    for (const d of dates) if (d <= target) best = d;
    return best;
  };

  const endKey = onOrBefore(thisMon) ?? dates[dates.length - 1];
  let baseKey = onOrBefore(lastMon);
  // Cold-start: no snapshot as far back as last Monday → use the earliest we have
  // (so short histories still produce a "since we started tracking" movement).
  if (!baseKey || baseKey >= endKey) baseKey = dates.find((d) => d < endKey);

  const end = trends[endKey];
  const base = baseKey ? trends[baseKey] : undefined;

  const pool: BiggestMover[] = [];

  // ── Video movers (weekly view gain) ──
  const endVideos = end?.videos ?? {};
  const baseVideos = base?.videos ?? {};
  for (const key of Object.keys(endVideos)) {
    const ev = endVideos[key];
    const endPlays = Number(ev?.plays ?? 0);
    if (endPlays <= 0) continue;
    const basePlays = Number(baseVideos[key]?.plays ?? 0);
    // Require a REAL prior-Monday baseline. Without one (basePlays 0) we can't
    // tell "genuinely new this week" from "not yet tracked", and showing a
    // lifetime count as a weekly gain would be wrong, such items wait until
    // they have a week of history, and cold-start falls through to the fallback.
    if (basePlays <= 0) continue;
    const gain = endPlays - basePlays;
    if (gain <= 0) continue;
    const s = videoSrc(ev?.platform ?? 'youtube');
    pool.push({
      kind: 'video', platform: s.platform, label: ev?.name || 'Video', src: s.label,
      value: endPlays, gain, unit: 'views', thumb: ev?.thumb || '',
      thumbColor: MOVER_BRAND_COLOR[s.platform] || '#444',
      delta: basePlays > 0 ? ((endPlays - basePlays) / basePlays) * 100 : null,
    });
  }

  // ── Track movers (weekly stream gain) ──
  const endTracks = end?.tracks ?? {};
  const baseTracks = base?.tracks ?? {};
  const meta = end?.tracks_meta ?? {};
  const liveById = new Map<string, StatTrack>();
  for (const t of stats.spotify?.top_tracks ?? []) {
    liveById.set(String(t.id || t.track_id || t.name || ''), t);
  }
  for (const id of Object.keys(endTracks)) {
    const endStreams = Number(endTracks[id] ?? 0);
    if (endStreams <= 0) continue;
    const baseStreams = Number(baseTracks[id] ?? 0);
    // Require a real prior-Monday baseline (see the video note above) so a
    // lifetime/28-day count never masquerades as a one-week stream gain.
    if (baseStreams <= 0) continue;
    const gain = endStreams - baseStreams;
    if (gain <= 0) continue;
    const lt = liveById.get(id);
    pool.push({
      kind: 'track', platform: 'spotify',
      label: meta[id]?.name || lt?.name || lt?.title || 'Untitled', src: 'Spotify',
      value: endStreams, gain, unit: 'streams',
      thumb: meta[id]?.art || (lt ? trackArtwork(lt) : ''),
      thumbColor: MOVER_BRAND_COLOR.spotify,
      delta: baseStreams > 0 ? ((endStreams - baseStreams) / baseStreams) * 100 : null,
    });
  }

  if (!pool.length) return liveMoverFallback(stats, limit);

  // The biggest mover = the most views/streams gained this week. Rank by absolute
  // weekly gain (concrete + comparable across new and old content; avoids the
  // %-explosion a tiny-baseline item would cause).
  pool.sort((a, b) => (b.gain ?? 0) - (a.gain ?? 0));
  return onePerPlatform(pool, limit);
}

/**
 * The spotlight's "top mover", the single highest positive-delta item across
 * the whole movers grid (dashboard-wiring.js:2303-2311). null when no positive
 * deltas exist yet (the spotlight then uses its no-delta fallback).
 */
export function topMoverOf(movers: ReadonlyArray<BiggestMover>): BiggestMover | null {
  // Prefer the biggest positive 30-day GROWTH (delta). But until ~30 days of
  // trend snapshots accumulate, movers carry no delta, only a raw value. In
  // that case fall back to the highest-value mover so the Spotlight's "Top mover"
  // shows the leading platform instead of "-" (the Spotlight gates its
  // "trending"/% claims on delta, so a value-only fallback never implies growth).
  let bestDelta: BiggestMover | null = null;
  let bestValue: BiggestMover | null = null;
  for (const m of movers) {
    if (typeof m.delta === 'number' && m.delta > 0) {
      if (!bestDelta || (bestDelta.delta ?? -Infinity) < m.delta) bestDelta = m;
    }
    if (typeof m.value === 'number' && m.value > 0) {
      if (!bestValue || (bestValue.value ?? -Infinity) < m.value) bestValue = m;
    }
  }
  return bestDelta || bestValue;
}

/**
 * The Spotlight's "Top mover", the PLATFORM whose audience grew the most over
 * the past ~30 days (2026-07-07, Ryan: was the highest-delta CONTENT item like a
 * single song). Growth = the percentage change in each platform's audience
 * (followers; subscribers for YouTube) between the latest daily_trends snapshot
 * and the one on/just-before ~30 days earlier. A percentage (not an absolute
 * count) so it's comparable across platforms of very different sizes, and it
 * matches the Spotlight's % delta chip.
 *
 * Cold-start: with no ~30-day-old snapshot yet, the earliest snapshot we have is
 * used as the baseline (a "since we started tracking" lift). With no usable
 * baseline at all, or no platform showing positive growth, it falls back to
 * the LARGEST platform by current audience with delta:null, so the Spotlight
 * still names a platform but makes no % claim (it gates its "trending"/% copy on
 * a non-null delta).
 *
 * @param trends the daily_trends snapshots (fetchDailyTrends()).
 * @param stats  live stats, for the no-baseline fallback's current audience.
 */
export function computePlatformTopMover(
  trends: Record<string, TrendSnapshot>,
  stats: DashboardStats,
): BiggestMover | null {
  const PLATFORMS: ReadonlyArray<{ key: MoverPlatform; label: string; metric: 'followers' | 'subscribers' }> = [
    { key: 'spotify', label: 'Spotify', metric: 'followers' },
    { key: 'instagram', label: 'Instagram', metric: 'followers' },
    { key: 'tiktok', label: 'TikTok', metric: 'followers' },
    { key: 'facebook', label: 'Facebook', metric: 'followers' },
    { key: 'youtube', label: 'YouTube', metric: 'subscribers' },
    { key: 'threads', label: 'Threads', metric: 'followers' },
  ];
  const snapAudience = (s: TrendPlatformSnapshot | undefined, metric: 'followers' | 'subscribers'): number =>
    Number((metric === 'subscribers' ? s?.subscribers : s?.followers) ?? 0);
  const liveAudience = (key: MoverPlatform): number => {
    switch (key) {
      case 'spotify': return Number(stats.spotify?.followers ?? 0);
      case 'instagram': return Number(stats.instagram?.followers ?? 0);
      case 'tiktok': return Number(stats.tiktok?.followers ?? 0);
      case 'facebook': return Number(stats.facebook?.followers ?? 0);
      case 'youtube': return Number(stats.youtube?.subscribers ?? 0);
      case 'threads': return Number(stats.threads?.followers ?? 0);
      default: return 0;
    }
  };
  const make = (p: { key: MoverPlatform; label: string }, value: number, delta: number | null): BiggestMover => ({
    kind: 'platform', platform: p.key, label: p.label, src: p.label,
    value, unit: 'followers', thumb: '',
    thumbColor: MOVER_BRAND_COLOR[p.key] || '#444', delta,
  });

  // ── 30-day growth path ──
  const dates = Object.keys(trends).sort();
  let best: BiggestMover | null = null;
  if (dates.length >= 2) {
    const endKey = dates[dates.length - 1];
    const cutoff = new Date();
    cutoff.setDate(cutoff.getDate() - 30);
    const cutoffIso = isoOf(cutoff);
    // Latest snapshot on/before ~30 days ago; else the earliest we have (cold-start).
    let baseKey: string | undefined;
    for (const d of dates) if (d <= cutoffIso && d < endKey) baseKey = d;
    if (!baseKey) baseKey = dates.find((d) => d < endKey);
    const end = trends[endKey];
    const base = baseKey ? trends[baseKey] : undefined;
    if (end && base) {
      for (const p of PLATFORMS) {
        const endVal = snapAudience(end[p.key], p.metric);
        const baseVal = snapAudience(base[p.key], p.metric);
        if (baseVal <= 0 || endVal <= baseVal) continue;
        const delta = ((endVal - baseVal) / baseVal) * 100;
        if (!best || (best.delta ?? -Infinity) < delta) best = make(p, endVal, delta);
      }
    }
  }
  if (best) return best;

  // ── Fallback: no usable baseline / no positive growth → largest platform by
  // current audience, no % claim (delta null). ──
  let bestVal: BiggestMover | null = null;
  for (const p of PLATFORMS) {
    const v = liveAudience(p.key);
    if (v <= 0) continue;
    if (!bestVal || bestVal.value < v) bestVal = make(p, v, null);
  }
  return bestVal;
}

// ── Most-recent release detail ────────────────────────────────────────────────
//
// VERIFICATION (per the strict rules), composited from 5 endpoints exactly as
// label-recent-release-wiring.js does:
//
//   1) Identity, GET /api/data/releases
//      Legacy caller (wiring:448): fetch(AUTH_WORKER + '/api/data/releases', {...})
//      Legacy read (wiring:417,420,423): setText('labelRecentTitle', r.name || '…');
//        var typeLabel = (r.type || 'Single'); setText('labelRecentDate', …r.release_date…)
//      Worker return (cntrl-auth-worker.js:18553): jsonResponse({ success:true, key, data })
//
//   2) Landing visits / Smart-link clicks, GET /release-analytics (stats worker)
//      Legacy caller (wiring:468): fetch(STATS_WORKER + '/release-analytics?slug='+…)
//      Legacy read (wiring:348-355): a.daily[d] (visits) ; a.clicks.daily[d].total (clicks)
//      Worker return (cntrl-stats-worker.js:4094): return jsonResponse(raViews);
//        // raViews = { total, daily:{date:n}, clicks:{ daily:{date:{total}} } }
//
//   3) Songstats aggregate, GET /api/release/songstats
//      Legacy caller (wiring:522): fetch(AUTH_WORKER + '/api/release/songstats?'+qs)
//      Legacy read (wiring:661,666,672,674): stats.aggregate.totalPlaylists /
//        .totalPlaylistReach / (.totalEditorial||sp.editorial) / sp.playlistsCurrent
//      Worker return (cntrl-auth-worker.js:21146): { ok:true, stats: normalized, top_playlists }
//
//   4) Fresh Spotify streams, GET /api/release/streams-spotify
//      Legacy caller (wiring:546): fetch(AUTH_WORKER+'/api/release/streams-spotify?spotify_track_id='+…)
//      Legacy read (wiring:551-552): if(typeof json.streams!=='number') return null; return json.streams;
//      Worker return (analytics.js:1939): return reply({ ok:true, streams:newStreams, … });
//
//   5) Fresh Spotify popularity, GET /api/spotify/track-popularity
//      Legacy caller (wiring:565): fetch(AUTH_WORKER+'/api/spotify/track-popularity?spotify_track_id='+…)
//      Legacy read (wiring:570-571): if(typeof json.popularity!=='number') return null; return json.popularity;
//      Worker return (cntrl-auth-worker.js:16666): return reply({ ok:true, popularity, … });
//

const SONGSTATS_PATH = '/api/release/songstats';

/** Title-case a release type ('single' → 'Single'); mirrors wiring:420-421. */
function titleCaseType(type: string | undefined): string {
  const t = type || 'Single';
  return t.charAt(0).toUpperCase() + t.slice(1).toLowerCase();
}

/** Lightweight slugify mirroring the wiring's slug(). */
function slugify(s: string): string {
  return String(s || '')
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');
}

/** Extract a Spotify track id from a smart-link value (url or bare id). */
function spotifyTrackId(release: ReleaseRecord): string {
  const raw = release.links && typeof release.links.spotify === 'string' ? release.links.spotify : '';
  if (!raw) return '';
  const m = raw.match(/track[/:]([A-Za-z0-9]+)/);
  return m ? m[1] : raw;
}

/** Sum the values of a {date: n} map whose date key is >= monthStart. */
function sumMtd(map: Record<string, number> | undefined, monthStart: string): number {
  if (!map) return 0;
  let total = 0;
  for (const d of Object.keys(map)) {
    if (d >= monthStart) total += Number(map[d]) || 0;
  }
  return total;
}

/** Sum clicks.daily[d].total for d >= monthStart (mirrors wiring:352-355). */
function sumClicksMtd(
  daily: Record<string, { total?: number }> | undefined,
  monthStart: string,
): number {
  if (!daily) return 0;
  let total = 0;
  for (const d of Object.keys(daily)) {
    if (d >= monthStart) total += Number(daily[d]?.total || 0);
  }
  return total;
}

/** Pick the most-recent RELEASED release (mirrors pickMostRecent, wiring:82). */
function pickMostRecent(list: ReadonlyArray<ReleaseRecord>): ReleaseRecord | null {
  const todayIso = new Date().toISOString().slice(0, 10);
  const released = list
    // Exclude unreleased AND future-dated items: a not-yet-out release must never
    // be picked as the most RECENT one. Without the date guard a future release
    // (e.g. "Same Side", Aug 7) sorted to the top, its slug had no traffic, and
    // the dashboard card's Landing Visits / Smart-Link Clicks painted "-".
    .filter((r) => {
      const d = r.release_date || r.date || '';
      return !r.unreleased && d !== '' && d <= todayIso;
    })
    .slice()
    .sort((a, b) => String(b.release_date || b.date || '').localeCompare(String(a.release_date || a.date || '')));
  return released[0] || null;
}

/** Pick the next UPCOMING release for the pill (mirrors pickUpcoming, wiring:148). */
function pickUpcoming(list: ReadonlyArray<ReleaseRecord>): ReleaseRecord | null {
  const todayIso = new Date().toISOString().slice(0, 10);
  const upcoming = list
    .filter((r) => {
      const d = r.release_date || r.date || '';
      return r.unreleased || (d && d > todayIso);
    })
    .slice()
    .sort((a, b) => String(a.release_date || a.date || '').localeCompare(String(b.release_date || b.date || '')));
  return upcoming[0] || null;
}

/** Unwrap the /api/data/releases blob → its array value. */
function unwrapReleases(json: ReleasesResponse | null): ReleaseRecord[] {
  if (!json) return [];
  let raw: unknown = json.data;
  if (raw && typeof raw === 'object' && !Array.isArray(raw)) {
    raw = (raw as { value?: unknown }).value;
  }
  if (typeof raw === 'string') {
    try {
      raw = JSON.parse(raw);
    } catch {
      raw = [];
    }
  }
  return Array.isArray(raw) ? (raw as ReleaseRecord[]) : [];
}

/**
 * Composite the "Most recent release" detail card. Returns null when the band
 * has no released release on file (the card shows its empty state). Each numeric
 * slot is null when its source returns nothing → the card renders "-".
 */
export async function fetchRecentRelease(
  connections?: Connections,
  bandNameArg?: string,
): Promise<RecentReleaseDetail | null> {
  const releasesJson = await apiGet<ReleasesResponse>('auth', '/api/data/releases').catch(() => null);
  const releases = unwrapReleases(releasesJson);
  /* recent-release-upcoming-fallback 2026-07-21 (Ryan: "release isn't making it
     to the home page"). pickMostRecent excludes unreleased/undated drops, so a
     band whose ONLY release is a pre-release (Date TBD) got the empty card even
     though the Releases and Music screens both show it. When there's no released
     drop, feature the next upcoming one and badge it UPCOMING (its stats read
     "-", which is honest for something that hasn't dropped). */
  let r = pickMostRecent(releases);
  const isUpcoming = !r;
  if (!r) r = pickUpcoming(releases);
  if (!r) return null;

  // Band name drives the smart-link analytics slug. Prefer the explicit arg (the
  // logged-in user's band_name), the React app never sets window.acBandName, so
  // relying on it skipped the analytics fetch entirely (Landing Visits / Smart-Link
  // Clicks came back "-" on the artist dashboard while the roster showed them).
  const bandName =
    (bandNameArg && bandNameArg.trim()) ||
    (typeof window !== 'undefined' && (window as { acBandName?: string }).acBandName) ||
    '';
  const relSlug = slugify(r.name || r.title || '');
  const releaseDate = r.release_date || r.date || null;
  const dayInCycle = releaseDate
    ? Math.max(0, Math.floor((Date.now() - new Date(releaseDate + 'T12:00:00').getTime()) / 86400000))
    : null;
  const monthStart = new Date().toISOString().slice(0, 7) + '-01';
  const trackId = spotifyTrackId(r);
  // Songstats Enterprise REQUIRES spotify_artist_id alongside spotify_track_id,
  // without it the playlist / reach / editorial / active fields come back zero
  // (paints as "-"). Fall back to the band-level Spotify connection id exactly
  // like the legacy fetchSongstats (label-recent-release-wiring.js:515-518).
  const artistId =
    r.spotify_artist_id ||
    (connections?.spotify as ConnectionBlob | undefined)?.id ||
    '';

  // Analytics (visits + clicks). Slug = band/release; only when band is known.
  let landingVisitsMtd: number | null = null;
  let smartLinkClicksMtd: number | null = null;
  if (bandName && relSlug) {
    const slug = slugify(bandName) + '/' + relSlug;
    const a = await apiGet<ReleaseAnalyticsResponse>(
      'stats',
      '/release-analytics?slug=' + encodeURIComponent(slug),
    ).catch(() => null);
    if (a) {
      landingVisitsMtd = sumMtd(a.daily, monthStart);
      smartLinkClicksMtd = sumClicksMtd(a.clicks?.daily, monthStart);
    }
  }

  // Songstats aggregate (playlists/reach/editorial/active) + fresh streams/pop.
  let spotifyStreams: number | null = null;
  let spotifyPopularity: number | null = null;
  let playlistsTotal: number | null = null;
  let playlistReach: number | null = null;
  let editorialPicks: number | null = null;
  let activePlaylists: number | null = null;

  if (trackId) {
    const qs =
      'spotify_track_id=' +
      encodeURIComponent(trackId) +
      (artistId ? '&spotify_artist_id=' + encodeURIComponent(artistId) : '');
    const [ss, fresh, pop] = await Promise.all([
      apiGet<SongstatsResponse>('auth', SONGSTATS_PATH + '?' + qs).catch(() => null),
      apiGet<StreamsSpotifyResponse>(
        'auth',
        '/api/release/streams-spotify?spotify_track_id=' + encodeURIComponent(trackId),
      ).catch(() => null),
      apiGet<TrackPopularityResponse>(
        'auth',
        '/api/spotify/track-popularity?spotify_track_id=' + encodeURIComponent(trackId),
      ).catch(() => null),
    ]);

    if (ss && ss.ok && ss.stats) {
      const sp = ss.stats.spotify || {};
      const agg = ss.stats.aggregate || {};
      spotifyStreams = sp.streams && sp.streams > 0 ? sp.streams : null;
      spotifyPopularity = typeof sp.popularity === 'number' ? sp.popularity : null;
      playlistsTotal = agg.totalPlaylists && agg.totalPlaylists > 0 ? agg.totalPlaylists : null;
      playlistReach = agg.totalPlaylistReach && agg.totalPlaylistReach > 0 ? agg.totalPlaylistReach : null;
      const ed = agg.totalEditorial || sp.editorial || 0;
      editorialPicks = ed > 0 ? ed : null;
      activePlaylists = sp.playlistsCurrent && sp.playlistsCurrent > 0 ? sp.playlistsCurrent : null;
    }
    // Fresh sources override Songstats when present (wiring:759-767).
    if (fresh && fresh.ok && typeof fresh.streams === 'number' && fresh.streams > 0) {
      spotifyStreams = fresh.streams;
    }
    if (pop && pop.ok && typeof pop.popularity === 'number' && pop.popularity >= 0) {
      spotifyPopularity = pop.popularity;
    }
  }

  // When the card IS the upcoming release, don't also render a pill pointing at itself.
  const up = isUpcoming ? null : pickUpcoming(releases);

  return {
    id: String(r.id ?? relSlug),
    title: r.name || r.title || 'Untitled release',
    type: titleCaseType(r.type),
    releaseDate,
    dayInCycle,
    artworkUrl: resolveArtworkUrl(r as unknown as ReleasesRelease) || r.artwork_url || '',
    landingVisitsMtd,
    smartLinkClicksMtd,
    spotifyStreams,
    spotifyPopularity,
    playlistsTotal,
    playlistReach,
    editorialPicks,
    activePlaylists,
    isUpcoming,
    upcoming: up
      ? {
          title: up.name || up.title || 'Untitled',
          date: up.release_date || up.date || null,
          artworkUrl: resolveArtworkUrl(up as unknown as ReleasesRelease) || up.artwork_url || '',
        }
      : null,
  };
}

// ── Top playlists (Audience) ──────────────────────────────────────────────────
//
// VERIFICATION (per the strict rules):
//   Legacy caller, dashboard-wiring.js:849-865 fetchTopPlaylists():
//     fetch(AUTH_WORKER + '/api/label/artist/' + encodeURIComponent(artistId) +
//           '/music?window=28D', { headers: authHeaders() })
//   Legacy client read, dashboard-wiring.js:861-862, 684:
//     if (!res || !res.ok || !res.data) return;
//     renderTopPlaylistsFromMusic(res.data.streaming || {});
//     var playlists = (music && Array.isArray(music.top_playlists)) ? music.top_playlists : [];
//     // per-row: p.source, p.editorial, p._days_since_added, p.track_name,
//     //          p.position, p.name, p.followers
//   Worker return, cntrl-auth-worker.js:11554 (inside `var streaming = {`):
//     top_playlists:  topPlaylistsMerged || [],
//   …and the envelope, cntrl-auth-worker.js:11633:
//     var __responseBody = { ok: true, data: { … streaming: streaming, … } };
//   Row build, cntrl-auth-worker.js:10919-10933 (name/followers/editorial/
//     added_at/track_name/position/source) + 10977 (_days_since_added).

/**
 * GET /api/label/artist/{id}/music?window=28D → the top playlist placements.
 * Mirrors fetchTopPlaylists(). Returns [] when the id is unknown or the
 * endpoint has nothing (the card shows its empty state).
 *
 * NOTE: {id} is the CNTRL **band-data user id** (the band PRIMARY/founder's id),
 * NOT the Spotify artist id and NOT necessarily the logged-in user.id. The worker
 * reads ALL the artist's data (songstats_audience, connections, releases) keyed by
 * this URL id (cntrl-auth-worker.js:9945-9951), and that data lives under the band
 * PRIMARY id (the silo key). The roster proves this: it passes the band's primary
 * user id (roster_memberships.artist_user_id, kernel.js:111) and renders 15
 * placements. Passing the band-data id (User.band_user_id) lands on the data AND
 * passes auth:
 *   • If the logged-in user IS the band primary, user.id === band_user_id → the
 *     worker's `isSelf` check (String(user.id) === String(artistId), :9874) passes.
 *   • If the logged-in user is a non-primary AV band-mate, isSelf is false, but the
 *     founder's id is in the caller's `_labelReachableArtists` set (kernel.js:533-555
 *     adds the band founder for any 'artist'/'band_member_av' membership), so the
 *     :9876 reachable check passes, and the data is keyed under that same founder id.
 * Passing the bare user.id only works for the primary; for a band-mate it passes
 * `isSelf` but reads the member's (empty) silo → the empty card bug this fixes.
 * The Spotify id is NOT this value, the worker resolves it from connections.spotify.id.
 * Legacy: dashboard-wiring.js:850-851 → `var u = window.ac.user; var artistId = u && u.id;`
 *
 * @param bandDataId the band's data id, User.band_user_id (primary), falling back
 *                   to User.id when band_user_id is absent.
 */
export async function fetchTopPlaylists(bandDataId: string): Promise<PlaylistPlacement[]> {
  if (!bandDataId) return [];
  const res = await apiGet<ArtistMusicResponse>(
    'auth',
    '/api/label/artist/' + encodeURIComponent(bandDataId) + '/music?window=28D',
  ).catch((err: unknown) => {
    // Don't silently empty the card, a 403 here means a wrong/forbidden id.
    const status = err instanceof ApiError ? err.status : '?';
    console.warn('[dashboard] top playlists fetch failed (status ' + status + ')', err);
    return null;
  });
  if (!res || !res.ok || !res.data) return [];
  const list = res.data.streaming?.top_playlists;
  return Array.isArray(list) ? list.slice(0, 15) : [];
}

/**
 * GET /api/label/artist/{id}/music, both the playlists AND the Spotify artist
 * summary, from ONE call (the endpoint returns both on `data.streaming`). The
 * Audience card's headline numbers (monthly listeners / followers / total streams
 * / editorial / top tracks) used to read the dashboard SNAPSHOT (stats.raw.spotify),
 * which is frequently empty even when this endpoint has the data, so we read them
 * from the same source that already powers the (working) playlists.
 *
 * Worker return, handleLabelArtistMusic (cntrl-auth-worker.js:11637): data.streaming
 * = { monthly_listeners, followers, streams_total, editorial_count, top_tracks,
 *     top_playlists, … }.
 *
 * `spotify` is undefined when the streaming block has NO usable summary, so the
 * caller can fall back to the snapshot (`live ?? snapshot`).
 */
export async function fetchArtistMusic(
  bandDataId: string,
): Promise<{ topPlaylists: PlaylistPlacement[]; spotify: SpotifyStats | undefined }> {
  if (!bandDataId) return { topPlaylists: [], spotify: undefined };
  const res = await apiGet<ArtistMusicResponse>(
    'auth',
    '/api/label/artist/' + encodeURIComponent(bandDataId) + '/music?window=28D',
  ).catch((err: unknown) => {
    const status = err instanceof ApiError ? err.status : '?';
    console.warn('[dashboard] artist music fetch failed (status ' + status + ')', err);
    return null;
  });
  const s = res && res.ok && res.data ? res.data.streaming : null;
  const topPlaylists = s && Array.isArray(s.top_playlists) ? s.top_playlists.slice(0, 15) : [];
  let spotify: SpotifyStats | undefined;
  if (s) {
    const tracks = Array.isArray(s.top_tracks) ? s.top_tracks : [];
    const ml = s.monthly_listeners ?? undefined;
    const fol = s.followers ?? undefined;
    const st = s.streams_total ?? undefined;
    const ed = s.editorial_count ?? undefined;
    // Only surface a live summary when it actually carries something; otherwise
    // leave undefined so the snapshot fallback wins.
    if (ml != null || fol != null || st != null || ed != null || tracks.length > 0) {
      spotify = {
        monthly_listeners: ml ?? undefined,
        followers: fol ?? undefined,
        streams_total: st ?? undefined,
        ss_playlists_editorial_current: ed ?? undefined,
        top_tracks: tracks.length > 0 ? tracks : undefined,
      };
    }
  }
  return { topPlaylists, spotify };
}

// ── Playlist artwork (lazy cover resolve) ─────────────────────────────────────
//
// VERIFICATION (per the strict rules):
//   Legacy caller, roster/js/music-wiring.js:887-893 (artHeaders =
//     Object.assign({}, authHeaders(false), { 'X-Band-Id': currentArtistId })):
//     fetch(AUTH_WORKER + '/api/spotify/playlist-artwork?playlist_id=' +
//           encodeURIComponent(item.pid), { headers: artHeaders })
//       .then(r => r.ok ? r.json() : null)
//       .then(d => { if (d && d.ok && d.url) { paintArt(item.el, d.url, …);
//                    writeArtCache(item.pid, d.url); } })
//   Worker route, cntrl-auth-worker.js:39273:
//     if (path === '/api/spotify/playlist-artwork' && method === 'GET') {
//       try { return await handleSpotifyPlaylistArtwork(request, env, db, user); } …
//   Worker return, cntrl-auth-worker.js handleSpotifyPlaylistArtwork (~16030,16047):
//     return reply({ ok: true, url: pickedUrl, cached: false });
//     // (cache hit: reply({ ok:true, url: cached.url, cached:true });
//     //  no images: reply({ ok:false, error:'Playlist has no images' }, 200))
//
// The self dashboard has no impersonation band context, so authHeaders() won't
// attach X-Band-Id from getCurrentBandId(), we attach it explicitly (the band
// PRIMARY/data id), mirroring the legacy artHeaders. The worker only gates on a
// valid bearer token (the route's `user`); the band id is along for the ride
// exactly like the legacy roster caller passes currentArtistId.

/**
 * GET {AUTH_WORKER}/api/spotify/playlist-artwork?playlist_id=<id> → the cover
 * URL the worker resolves via the Spotify Web API (+ oEmbed fallback, 7d D1
 * cache). Mirrors music-wiring.js:887-893. Returns the url when the worker says
 * `{ ok:true, url }`, else null (best-effort, the caller keeps the placeholder).
 *
 * @param playlistId the Spotify playlist id (PlaylistPlacement.playlist_id).
 * @param bandId     the band's data id (X-Band-Id), User.band_user_id || User.id.
 */
export async function fetchPlaylistArtwork(
  playlistId: string,
  bandId: string,
): Promise<string | null> {
  if (!playlistId) return null;
  const headers: Record<string, string> = { ...authHeaders('GET') };
  if (bandId) headers['X-Band-Id'] = bandId;
  try {
    const res = await fetch(
      AUTH_WORKER + '/api/spotify/playlist-artwork?playlist_id=' + encodeURIComponent(playlistId),
      { method: 'GET', headers, cache: 'no-store' },
    );
    if (!res.ok) return null;
    const d = (await res.json()) as { ok?: boolean; url?: string };
    return d && d.ok && d.url ? d.url : null;
  } catch {
    return null;
  }
}

// ── To-do (dashboard tasks) ───────────────────────────────────────────────────
//
// VERIFICATION (per the strict rules), composited from two blobs exactly as
// artist-todo-wiring.js aggregateTasks (~283-369):
//   Legacy callers, artist-todo-wiring.js:646 + :650:
//     fetch(AUTH + '/api/data/standalone_tasks' + bust, { headers, cache:'no-store' })
//     fetch(AUTH + '/api/data/releases' + bust,        { headers, cache:'no-store' })
//   Legacy client read (standalone), :285-289:
//     task: t.title || t.task || '(untitled)',  due: t.due_date || t.dueDate || null,
//     status: String(t.status || 'open').toLowerCase(), done: !isOpen
//   Legacy client read (release checklist), :305-369:
//     var list = (Array.isArray(r.checklist) && r.checklist) || …;
//     task: item.task || item.label || item.title, due: item.date || item.due_date,
//     releaseTitle: r.name || r.title || ''     // ← the sub-line
//   Worker return (both), cntrl-auth-worker.js:18553: jsonResponse({ success,key,data })
//   Open/closed, statusIsOpen (:166-169): closed when status ∈ {done,completed,approved}.
//   Overdue, isOverdue (:147-157): due-date midnight < today midnight (computed here).

const CLOSED_STATUSES = new Set(['done', 'completed', 'approved']);

/** statusIsOpen mirror, a task is open unless its status is a closed one. */
function statusIsOpen(status: string | undefined): boolean {
  return !CLOSED_STATUSES.has(String(status || 'open').toLowerCase());
}

/** isOverdue mirror, due date (midnight) is strictly before today (midnight). */
function isOverdue(due: string | null): boolean {
  if (!due) return false;
  const d = new Date(due + 'T00:00:00');
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  return d.getTime() < today.getTime();
}

/** Unwrap a user_data blob → its array value (standalone_tasks / releases). */
function unwrapArray<T>(json: unknown): T[] {
  let raw: unknown = json;
  for (let i = 0; i < 4; i++) {
    if (raw && typeof raw === 'object' && !Array.isArray(raw)) {
      const obj = raw as Record<string, unknown>;
      if (obj.data !== undefined) {
        raw = obj.data;
        continue;
      }
      if (obj.value !== undefined) {
        raw = obj.value;
        continue;
      }
    }
    break;
  }
  if (typeof raw === 'string') {
    try {
      raw = JSON.parse(raw);
    } catch {
      raw = [];
    }
  }
  return Array.isArray(raw) ? (raw as T[]) : [];
}

/**
 * Aggregate the dashboard To-do list from standalone_tasks + each release's
 * checklist (no combined endpoint exists). Returns OPEN tasks sorted by due
 * date. Empty array → the card shows "You're all caught up".
 *
 * @param openOnly when true (default) drop completed tasks, like the card.
 */
/** Initials from a display name ("Ryan Ward" → "RW"). */
function _todoInitials(name: string): string {
  return name.trim().split(/\s+/).map((w) => w[0] || '').join('').slice(0, 2).toUpperCase();
}
/** Pull assigner (initiator) → assignee + avatars off a raw task/checklist record.
 *  Standalone tasks carry initiator{name,avatar_url} + assignees[{name,avatarUrl}]
 *  (TaskCreateModal); release-checklist items may carry initiator/from + assignee.
 *  Defensive across both shapes; missing avatars fall back to initials at render. */
function _todoPeople(raw: Record<string, unknown>): Pick<TodoTask, 'assigner' | 'assignee' | 'assignerAvatar' | 'assigneeAvatar' | 'assignerInitials' | 'assigneeInitials' | 'status'> {
  const init = (raw.initiator || raw.from || {}) as { name?: string; avatar_url?: string; avatarUrl?: string };
  const asgArr = Array.isArray(raw.assignees) ? (raw.assignees as Array<Record<string, unknown>>) : [];
  const a0 = (asgArr[0] || (raw.assignee && typeof raw.assignee === 'object' ? (raw.assignee as Record<string, unknown>) : {})) as Record<string, unknown>;
  const assigner = String(init.name || '');
  const assignee = String((a0.name as string) || (a0.firstName as string) || (typeof raw.assignee === 'string' ? raw.assignee : '') || '');
  return {
    assigner,
    assignee,
    assignerAvatar: String(init.avatar_url || init.avatarUrl || ''),
    assigneeAvatar: String((a0.avatarUrl as string) || (a0.avatar_url as string) || (a0.photo_url as string) || ''),
    assignerInitials: _todoInitials(assigner),
    assigneeInitials: _todoInitials(assignee),
    status: String((raw.status as string) || ''),
  };
}

export async function fetchTodoTasks(openOnly: boolean = true): Promise<TodoTask[]> {
  const [standaloneJson, releasesJson] = await Promise.all([
    apiGet<unknown>('auth', '/api/data/standalone_tasks').catch(() => null),
    apiGet<unknown>('auth', '/api/data/releases').catch(() => null),
  ]);

  const out: TodoTask[] = [];

  // Standalone tasks.
  const standalone = unwrapArray<StandaloneTaskRecord>(standaloneJson);
  standalone.forEach((t, i) => {
    const done = !statusIsOpen(t.status) || t.done === true || t.completed === true;
    const due = t.due_date || t.dueDate || null;
    out.push({
      id: t.id || 'st_' + i,
      task: t.title || t.task || '(untitled)',
      subtitle: '',
      due,
      done,
      overdue: !done && isOverdue(due),
      source: 'standalone',
      ..._todoPeople(t as unknown as Record<string, unknown>),
    });
  });

  // Release rollout tasks (each release's checklist[]).
  const releases = unwrapArray<ReleaseRecord>(releasesJson);
  releases.forEach((r, ri) => {
    const list =
      (Array.isArray((r as { checklist?: unknown }).checklist) &&
        ((r as { checklist?: ReleaseChecklistItem[] }).checklist as ReleaseChecklistItem[])) ||
      (Array.isArray((r as { prep_checklist?: unknown }).prep_checklist) &&
        ((r as { prep_checklist?: ReleaseChecklistItem[] }).prep_checklist as ReleaseChecklistItem[])) ||
      (Array.isArray((r as { tasks?: unknown }).tasks) &&
        ((r as { tasks?: ReleaseChecklistItem[] }).tasks as ReleaseChecklistItem[])) ||
      (Array.isArray((r as { todos?: unknown }).todos) &&
        ((r as { todos?: ReleaseChecklistItem[] }).todos as ReleaseChecklistItem[])) ||
      null;
    if (!list) return;
    list.forEach((item, ci) => {
      const done =
        item.done === true || item.completed === true || (!!item.status && !statusIsOpen(item.status));
      const due = item.date || item.due_date || null;
      out.push({
        id: (r.id != null ? r.id : ri) + ':' + ci,
        task: item.task || item.label || item.title || '(untitled)',
        subtitle: r.name || r.title || '',
        due,
        done,
        overdue: !done && isOverdue(due),
        source: 'release',
        ..._todoPeople(item as unknown as Record<string, unknown>),
      });
    });
  });

  const filtered = openOnly ? out.filter((t) => !t.done) : out;
  return filtered.sort((a, b) => (a.due || '9999').localeCompare(b.due || '9999'));
}

// ── Top cities ("Where your audience lives") ──────────────────────────────────
//
// VERIFICATION (per the strict rules):
//   Legacy caller, top-cities-wiring.js:131-132:
//     var url = AUTH_WORKER + '/api/booking/top-cities';
//     fetch(url, { headers: authHeaders() })
//   Legacy client read, render() (:165-179) + rowHtml (:332-377):
//     payload.success / payload.total_audience / payload.markets_tracked /
//     payload.sources{spotify,ig,fb,merch,email} / payload.cities[] where each
//     city = { city, total, spotify, ig, fb, last_show, next_show, action }.
//   Worker return, GET /api/booking/top-cities → { success:true, total_audience,
//     markets_tracked, sources, cities }.

/** One row in the Top Cities table (a market's cross-platform audience). */
export interface TopCity {
  readonly city: string;
  readonly total?: number;
  readonly spotify?: number;
  readonly ig?: number;
  /** antenna-cities 2026-07-17: Antenna followers in this city (was fb). */
  readonly antenna?: number;
  readonly merch?: number;
  readonly email?: number;
  /** ISO date of the last show played there, or null/absent → "Never". */
  readonly last_show?: string | null;
  /** ISO date of an upcoming booked show there (→ the "✓ <date>" action). */
  readonly next_show?: string | null;
  /** Audience-temperature CTA: 'book' | 'warm' | 'route'. */
  readonly action?: string;
}

/** The /api/booking/top-cities payload. */
export interface TopCitiesData {
  readonly success?: boolean;
  readonly total_audience?: number;
  readonly markets_tracked?: number;
  /** Per-platform sync state: spotify/ig/antenna/merch/email → 'live'|'syncing'|'off'. */
  readonly sources?: Record<string, string>;
  readonly cities?: ReadonlyArray<TopCity>;
}

/**
 * GET /api/booking/top-cities → the cross-referenced audience-by-city table.
 * Mirrors top-cities-wiring.js fetchTopCities (:131). Returns an empty payload on
 * failure so the section renders its empty state (Bible 06.4).
 */
export async function fetchTopCities(refresh = false): Promise<TopCitiesData> {
  // refresh=1 forces the worker to recompute (busting its 6h top_cities_cache),
  // used after a connection change so a disconnect/reconnect reflects instantly
  // instead of serving the pre-change cache.
  const path = '/api/booking/top-cities' + (refresh ? '?refresh=1' : '');
  const res = await apiGet<TopCitiesData>('auth', path).catch(() => null);
  return res && res.success ? res : { cities: [] };
}

// ── Aggregate loader ──────────────────────────────────────────────────────────

/** Everything the dashboard needs, fetched together. */
export interface DashboardData {
  readonly user: User;
  readonly connections: Connections;
  readonly stats: DashboardSpotlight;
  /**
   * Live spotlight background photo (base64 data URL), or '' when none uploaded.
   * Mirrors applySpotlightPhoto()'s photo3→photo2→photo1 precedence.
   */
  readonly spotlightPhoto: string;
  /** Ranked #1-#5 biggest movers (empty until trends + stats exist). */
  readonly movers: ReadonlyArray<BiggestMover>;
  /** Spotlight "Top mover", the platform with the biggest 30-day audience
   *  growth (null when no platforms/audience yet). */
  readonly topMover: BiggestMover | null;
  readonly opportunities: ReadonlyArray<Opportunity>;
  /** Contact-id → avatar url, to resolve opp invitee photos at render. */
  readonly inviteeAvatars: Record<string, string>;
  readonly upcomingShows: ReadonlyArray<UpcomingShow>;
  readonly merch: MerchSummary;
  /** Most-recent-release detail, or null when no release is on file. */
  readonly recentRelease: RecentReleaseDetail | null;
  /** Top playlist placements for the Audience section (empty when none). */
  readonly topPlaylists: ReadonlyArray<PlaylistPlacement>;
  /** Spotify headline summary read from the music endpoint (same source as the
   *  playlists). Undefined when that endpoint has no summary, caller falls back
   *  to the dashboard snapshot (stats.raw.spotify). */
  readonly spotifyLive?: SpotifyStats;
  /** Open to-do tasks (empty when caught up). */
  readonly todos: ReadonlyArray<TodoTask>;
  /** "Where your audience lives", cross-platform audience by city. */
  readonly topCities: TopCitiesData;
}

/* ── Two-phase dashboard load (boot-perf 2026-07-14, Dev request) ─────────────
   The old fetchDashboardData was ONE Promise.all of eleven fetches, and the
   boot mask (dataCache → acHideLoader) waited for ALL of them, so the
   SLOWEST third-party aggregation (stats /assets ~2.0s, label music ~2.0s,
   release-analytics ~1.8s, proxy/stats ~1.0s; see docs/BOOT-PERF-ANALYSIS.md)
   held first paint hostage while every own-D1 call was done in ~250ms.

   Now:
     · FAST phase, identity + the quick own-D1 reads (trends, opportunities,
       contacts, shows, todos, top-cities). fetchDashboardDataFast() resolves
       on these alone with honest placeholders for the slow sections → the
       mask hides ~2s earlier and the shell paints.
     · SLOW phase, the third-party aggregations (platform stats, spotlight
       photo, merch summary, recent-release analytics, artist music). They
       start at the SAME moment they always did (right after identity, not
       after the fast wave) and hydrate the already-painted dashboard when
       they land.
   Both phases share one in-flight memo, so running the two fetchers
   concurrently costs ZERO duplicate requests. The memo self-clears after
   resolution so SWR background revalidation still hits the server fresh. */

interface DashboardFastParts {
  readonly user: User;
  readonly connections: Connections;
  readonly bandDataId: string;
  readonly trends: Record<string, TrendSnapshot>;
  readonly opportunities: ReadonlyArray<Opportunity>;
  readonly contacts: ReadonlyArray<Contact>;
  readonly rawUpcoming: ReadonlyArray<UpcomingShow>;
  readonly todos: ReadonlyArray<TodoTask>;
  readonly topCities: TopCitiesData;
}

const emptySpotlight = (): DashboardSpotlight => ({
  totalFollowers: 0,
  spotifyFollowers: 0,
  monthlyListeners: 0,
  streamsTotal: 0,
  topMover: null,
  raw: {} as DashboardStats,
}) as DashboardSpotlight;

const emptyMerch = (): MerchSummary => ({
  currency: 'USD',
  salesToday: 0,
  sales30d: 0,
  pendingOrders: 0,
  bestsellers: [],
  posOnly: false,
  source: 'none',
}) as MerchSummary;

/* One identity flight per boot: user + connections gate BOTH phases, so they
   must never be fetched twice just because two fetchers run concurrently. */
let _identP: Promise<readonly [User, Connections]> | null = null;
function fetchIdentOnce(): Promise<readonly [User, Connections]> {
  if (_identP) return _identP;
  const p = Promise.all([fetchCurrentUser(), fetchConnections()] as const);
  _identP = p;
  void p.finally(() => { setTimeout(() => { if (_identP === p) _identP = null; }, 3000); });
  return p;
}

/* consolidation 2026-07-14 (Dev consult): every /api/data/<key> the boot
   touches, batched into ONE request via the worker's existing ?keys=
   endpoint. 'releases' rides along for the slow phase's recent-release
   read; apiGet serves all of them from the prime (see lib/api.ts). */
const BOOT_DATA_KEYS = ['daily_trends', 'opportunities', 'schedule', 'contacts', 'releases', 'standalone_tasks'] as const;

/* One fast-wave flight per boot, shared by the fast and full fetchers. */
let _fastP: Promise<DashboardFastParts> | null = null;
function fetchFastParts(): Promise<DashboardFastParts> {
  if (_fastP) return _fastP;
  const p = (async (): Promise<DashboardFastParts> => {
    /* Fires immediately, needs only the cached token + band id, so it
       overlaps the identity round-trip instead of waiting behind it. */
    const primeP = primeDataBatch([...BOOT_DATA_KEYS]).catch(() => undefined);
    const [user, connections] = await fetchIdentOnce();
    // Top Playlists reads ALL data keyed by the URL id, and the band's data lives
    // under the band PRIMARY id (the silo key), so pass band_user_id (the founder's
    // id), falling back to user.id when absent. Passing the bare user.id breaks for
    // non-primary band-mates (lands on their empty silo). See fetchTopPlaylists.
    const bandDataId = user.band_user_id != null ? String(user.band_user_id) : user.id != null ? String(user.id) : '';
    await primeP; // batch landed (or failed open), the reads below hit the prime
    /* dashboard-resilience 2026-07-06: each section degrades ALONE, one
       rejection must never blank the dashboard. */
    const [trends, opportunities, contacts, rawUpcoming, todos, topCities] = await Promise.all([
      fetchDailyTrends().catch(() => ({}) as Record<string, TrendSnapshot>),
      fetchOpportunities().catch(() => [] as Opportunity[]),
      fetchContacts().catch(() => [] as Contact[]),
      fetchUpcomingShows(20).catch(() => [] as UpcomingShow[]),
      fetchTodoTasks().catch(() => [] as TodoTask[]),
      fetchTopCities().catch(() => ({ cities: [] }) as TopCitiesData),
    ]);
    return { user, connections, bandDataId, trends, opportunities, contacts, rawUpcoming, todos, topCities };
  })();
  _fastP = p;
  void p.finally(() => { setTimeout(() => { if (_fastP === p) _fastP = null; }, 3000); });
  return p;
}

interface DashboardSlowParts {
  readonly stats: DashboardSpotlight;
  readonly spotlightPhoto: string;
  readonly merch: MerchSummary;
  readonly recentRelease: RecentReleaseDetail | null;
  readonly music: { topPlaylists: ReadonlyArray<PlaylistPlacement>; spotify?: DashboardData['spotifyLive'] };
}

function assembleDashboard(f: DashboardFastParts, s: DashboardSlowParts): DashboardData {
  // Biggest Movers: a card shows its REAL content image (a top post/track
  // thumbnail) when one exists, and the platform BRAND COLOR otherwise. We do
  // NOT substitute a generic band photo, a press shot reads like it's platform
  // content when it isn't (confusing). Honest: real image or brand color.
  const movers = computeBiggestMovers(f.trends, s.stats.raw, 5);
  // Spotlight "Top mover" = the PLATFORM with the biggest 30-day audience growth
  // (2026-07-07, Ryan), computed here where the trend snapshots are in scope.
  const topMover = computePlatformTopMover(f.trends, s.stats.raw);
  const inviteeAvatars = buildInviteeAvatarMap(f.contacts);
  // Merge calendar-type opportunities (rehearsals etc.) into Upcoming so it
  // shows every type, not just schedule shows/holds.
  const upcomingShows = mergeUpcoming(f.rawUpcoming, f.opportunities, 5);
  return {
    user: f.user,
    connections: f.connections,
    stats: s.stats,
    spotlightPhoto: s.spotlightPhoto,
    movers,
    topMover,
    opportunities: f.opportunities,
    inviteeAvatars,
    upcomingShows,
    merch: s.merch,
    recentRelease: s.recentRelease,
    topPlaylists: s.music.topPlaylists,
    spotifyLive: s.music.spotify,
    todos: f.todos,
    topCities: f.topCities,
  };
}

/**
 * FAST phase only, resolves as soon as identity + the quick own-D1 reads
 * land (~1.4s vs ~3.2s for the full load). Slow sections carry honest
 * placeholders ('not connected' merch, zero stats, no release detail); the
 * full fetch hydrates them moments later.
 */
export async function fetchDashboardDataFast(): Promise<DashboardData> {
  const f = await fetchFastParts();
  return assembleDashboard(f, {
    stats: emptySpotlight(),
    spotlightPhoto: '',
    merch: emptyMerch(),
    recentRelease: null,
    music: { topPlaylists: [] as PlaylistPlacement[], spotify: undefined },
  });
}

/**
 * FULL load, same complete DashboardData as always. The slow third-party
 * batch starts the moment identity resolves (unchanged timing), in parallel
 * with the fast wave, and the shared memos guarantee nothing is fetched
 * twice when this runs concurrently with fetchDashboardDataFast().
 */
export async function fetchDashboardData(): Promise<DashboardData> {
  const [user, connections] = await fetchIdentOnce();
  const bandDataId = user.band_user_id != null ? String(user.band_user_id) : user.id != null ? String(user.id) : '';
  const slowP = Promise.all([
    /* dashboard-resilience 2026-07-06: each degrades alone (see fast wave). */
    fetchDashboardStats(connections).catch(() => emptySpotlight()),
    fetchSpotlightPhoto().catch(() => ''),
    fetchMerchSummary(connections).catch(() => emptyMerch()),
    fetchRecentRelease(connections, user.band_name || '').catch(() => null),
    fetchArtistMusic(bandDataId).catch(() => ({ topPlaylists: [] as PlaylistPlacement[], spotify: undefined })),
  ] as const);
  const f = await fetchFastParts();
  const [stats, spotlightPhoto, merch, recentRelease, music] = await slowP;
  return assembleDashboard(f, { stats, spotlightPhoto, merch, recentRelease, music });
}

// ── Dev-only wiring example (NOT executed) ────────────────────────────────────
// Type-checked proof that the models line up with how CD's redesigned Dashboard
// will consume the data layer. Uncomment inside a component to wire it up:
//
//   import { useDashboardData } from '../lib';
//
//   function DashboardDataExample() {
//     const { data, loading, error } = useDashboardData();
//     if (loading || error || !data) return null;
//     const greetingName: string = data.user.display_name;
//     const followers: number = data.stats.totalFollowers;
//     const nextShowTitle: string | undefined = data.upcomingShows[0]?.title;
//     const merchToday: number = data.merch.salesToday;
//     const oppCount: number = data.opportunities.length;
//     void [greetingName, followers, nextShowTitle, merchToday, oppCount];
//     return null;
//   }
